import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import { showActivity, hideActivity } from '../activity';
import Planner from '../components/Planner';
import CommentPlans from '../components/CommentPlans';
import { useKarmaPostingAccounts, KarmaModeNotice, KarmaGenerateButton, isKarmaIdeaContent } from '../components/KarmaPostIdeas';
import MediaLightbox from '../components/MediaLightbox';
import TagFilter from '../components/TagFilter';
import { API } from '../config';
import { notifyError, notify } from '../notify';


// The sub's own title rules (scraped from Reddit's post_requirements). Same
// semantics as the extension's check, surfaced here so a title that would be
// auto-removed is fixed while it's being written, not after it's posted.
function titleRuleViolations(title, sub) {
  const t = (title || '').trim();
  if (!t || !sub) return [];
  const out = [];
  const lower = t.toLowerCase();
  if (sub.title_min_length && t.length < sub.title_min_length) out.push(`at least ${sub.title_min_length} characters`);
  if (sub.title_max_length && t.length > sub.title_max_length) out.push(`at most ${sub.title_max_length} characters`);
  const req = Array.isArray(sub.title_required_strings) ? sub.title_required_strings : [];
  if (req.length && !req.some(r => lower.includes(String(r).toLowerCase()))) out.push(`must contain one of: ${req.join(' / ')}`);
  const ban = Array.isArray(sub.title_blacklisted_strings) ? sub.title_blacklisted_strings : [];
  const hit = ban.filter(b => containsBanned(lower, b));
  if (hit.length) out.push(`remove banned word(s): ${hit.join(', ')}`);
  const rx = Array.isArray(sub.title_regexes) ? sub.title_regexes : [];
  if (rx.length) {
    let usable = 0, matched = false;
    for (const r of rx) { try { usable++; if (new RegExp(r, 'i').test(t)) matched = true; } catch (e) { usable--; } }
    if (usable > 0 && !matched) out.push("doesn't match the sub's required title format");
  }
  return out;
}

// Reddit matches a banned string as a whole word ("hi" doesn't block
// "chilling"); one that starts or ends with punctuation ("?", "[f]") matches
// anywhere. Same rule as backend/helpers/titleRules.js.
function containsBanned(lowerTitle, banned) {
  const b = String(banned || '').toLowerCase();
  if (!b) return false;
  const wordChar = (ch) => /[\p{L}\p{N}]/u.test(ch);
  const esc = b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp((wordChar(b[0]) ? '(?:^|[^\\p{L}\\p{N}])' : '') + esc + (wordChar(b[b.length - 1]) ? '(?:$|[^\\p{L}\\p{N}])' : ''), 'u');
  return re.test(lowerTitle);
}

// What a sub can take (post.sub_caps from the server's shared rules —
// backend/helpers/eligibility.js). Mirrors mediaFit there; returns why this
// content can only fail in this sub, or null.
const EXPLICIT_RANK = { 'non-nude': 0, 'semi-nude': 1, 'nude': 2, 'explicit': 3 };
function mediaFromUrl(url) {
  const ext = String(url || '').split('?')[0].split('.').pop().toLowerCase();
  if (ext === 'gif') return 'gif';
  if (['mp4', 'mov', 'webm', 'm4v', 'avi'].includes(ext)) return 'video';
  return url ? 'image' : null;
}
function contentFitIssue(mediaType, caps, { count = 0, explicitness = null } = {}) {
  const c = caps;
  if (!c) return null;
  if (c.dead) return 'sub is banned/deleted (404)';
  if (!mediaType) return c.images === false ? 'sub does not accept image posts' : null;
  if (mediaType === 'video') { if (c.videos === false) return 'sub does not allow video uploads'; }
  else if (c.images === false) return 'sub does not accept image posts';
  if (mediaType === 'gif' && c.gif === false) return 'sub removed a GIF before';
  if (mediaType === 'carousel') {
    if (c.galleries === false) return 'sub does not allow galleries';
    if (c.gallery_max && count > c.gallery_max) return `sub allows at most ${c.gallery_max} images per gallery`;
  }
  if (c.max_explicit_rank != null && explicitness) {
    const r = EXPLICIT_RANK[String(explicitness).toLowerCase()];
    if (r != null && r > c.max_explicit_rank) return `sub removed ${Object.keys(EXPLICIT_RANK)[c.max_explicit_rank + 1]} content before`;
  }
  // backend/helpers/eligibility.js mediaFit: SFW subs take non-nude photos only
  if (c.sfw && explicitness && String(explicitness).toLowerCase() !== 'non-nude') return 'SFW sub: only photos marked non-nude';
  return null;
}
// Live fit of a scheduled post as it currently stands in the UI.
function postFitIssue(post) {
  if (!post || post.is_profile_post || !post.sub_caps) return null;
  const media = post.content_type === 'carousel' ? 'carousel' : mediaFromUrl(post.content_url);
  // Explicitness is only known for the item the server saw.
  const expl = post.vault_item_id && post.vault_item_id === post._fit_vault_id ? post.content_explicitness : null;
  return contentFitIssue(media, post.sub_caps, { count: (post.carousel_items || []).length, explicitness: expl });
}

const PostTitleInput = memo(({ postId, sessionId, initialValue, disabled, onSave }) => {
  const [value, setValue] = useState(initialValue || '');
  const timerRef = useRef(null);

  const debouncedSave = useCallback((val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (val !== initialValue) onSave(sessionId, postId, val);
    }, 800);
  }, [sessionId, postId, initialValue, onSave]);

  return (
    <input
      placeholder="Post title"
      value={value}
      disabled={disabled}
      onChange={e => {
        setValue(e.target.value);
        debouncedSave(e.target.value);
      }}
      onBlur={() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (value !== initialValue) onSave(sessionId, postId, value);
      }}
      style={{ width: '100%', ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
    />
  );
});

// A model with 2+ accounts: a tab per account (and All) with its open
// sessions, so two accounts' sessions don't mix on one page
function AccountTabs({ accounts, sessions, activeId, onPick }) {
  const tabs = [{ id: 'all', label: 'All accounts', ids: accounts.map(a => a.id) },
    ...accounts.map(a => ({ id: a.id, label: `u/${a.reddit_username}`, ids: [a.id] }))];
  return (
    <div role="group" aria-label="Accounts"
      style={{ display: 'flex', flexWrap: 'wrap', width: 'fit-content', maxWidth: '100%', gap: '2px', padding: '3px', marginBottom: '16px', background: '#141414', border: '1px solid #2a2a2a', borderRadius: '10px' }}>
      {tabs.map(t => {
        const active = t.id === activeId;
        const open = sessions.filter(s => s.status !== 'posted' && t.ids.includes(s.account_id)).length;
        return (
          <button key={t.id} aria-pressed={active} className="planner-seg" onClick={() => onPick(t.id)} title={`${open} open session${open === 1 ? '' : 's'}`}
            style={{ background: active ? '#ff4500' : 'transparent', color: active ? '#fff' : '#999' }}>
            {t.label} · {open}
          </button>
        );
      })}
    </div>
  );
}

function Scheduler({ selectedCreator, creatorAccountIds, role }) {
  const [sessions, setSessions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subreddits, setSubreddits] = useState([]);
  const [postingLists, setPostingLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [intervalStatus, setIntervalStatus] = useState({});
  const [vaultData, setVaultData] = useState({});
  const [vaultModal, setVaultModal] = useState(null);
  const [editingSessionName, setEditingSessionName] = useState(null);
  const [editingSessionNameValue, setEditingSessionNameValue] = useState('');
  // The preview shows the LIVE session (derived by id), so repicks/removals
  // made anywhere appear in it immediately.
  const [previewSessionId, setPreviewSessionId] = useState(null);
  const previewSession = previewSessionId ? (sessions.find(s => s.id === previewSessionId) || null) : null;
  const setPreviewSession = (v) => setPreviewSessionId(v && typeof v === 'object' ? v.id : (v || null));
  // Full-size viewer: { sessionId, postId, index }
  const [lightbox, setLightbox] = useState(null);
  const [autofilling, setAutofilling] = useState(null); // session id being auto-filled
  const [autofillModal, setAutofillModal] = useState(null); // session id whose settings popup is open
  const [autofillGifPct, setAutofillGifPct] = useState(30);
  const [autofillCarousels, setAutofillCarousels] = useState(0);
  const [autofillMode, setAutofillMode] = useState('auto'); // 'auto' = carousels wherever the sub allows galleries | 'none' | 'custom'
  const [autofillPreview, setAutofillPreview] = useState(null);
  // Suggested values for the run, computed by the server from the vault and the session's subs
  useEffect(() => {
    if (!autofillModal) { setAutofillPreview(null); return; }
    axios.get(`${API}/api/scheduler/${autofillModal}/autofill-preview`).then(r => {
      setAutofillPreview(r.data);
      setAutofillGifPct(r.data.suggested_gif_pct ?? 30);
      setAutofillCarousels(r.data.suggested_carousels ?? 0);
      setAutofillMode('auto');
    }).catch(() => setAutofillPreview(null));
  }, [autofillModal]);
  // Subs in each account's open sessions that another account of the model
  // uses (a second account's): account id → { subreddit_id: why }. Marks only —
  // a failed read shows none, and auto-fill still leaves those slots empty.
  const [closedSubs, setClosedSubs] = useState({});
  const openSessionAccounts = [...new Set(sessions.filter(s => s.status !== 'posted').map(s => s.account_id))].sort().join(',');
  useEffect(() => {
    if (!openSessionAccounts) return;
    let current = true;
    Promise.all(openSessionAccounts.split(',').map(id => axios.get(`${API}/api/scheduler/closed-subs`, { params: { account_id: id } })
      .then(r => [id, r.data.closed || {}])
      .catch(err => { console.error('closed subs:', err.message); return [id, {}]; })))
      .then(pairs => { if (current) setClosedSubs(Object.fromEntries(pairs)); });
    return () => { current = false; };
  }, [openSessionAccounts]);
  const closedWhy = (accountId, subredditId) => closedSubs[accountId]?.[subredditId] || null;
  const [repicking, setRepicking] = useState(null); // post id being repicked
  const [repickingAll, setRepickingAll] = useState(null); // session id being mass-repicked
  const [form, setForm] = useState({ account_id: '', scheduled_date: new Date().toISOString().split('T')[0], name: '', spoof_preset: 'medium', spoof_preset_gif: 'medium', include_profile_post: true });
  const [rulesModal, setRulesModal] = useState(null);
  const [editingRules, setEditingRules] = useState(false);
  const [rulesValue, setRulesValue] = useState('');
  const [editingTitleReq, setEditingTitleReq] = useState(null);
  const [titleReqValue, setTitleReqValue] = useState('');
  const [subPickerModal, setSubPickerModal] = useState(null);
  const [subPickerSearch, setSubPickerSearch] = useState('');
  const [subPickerTags, setSubPickerTags] = useState([]);
  const [subPickerSelected, setSubPickerSelected] = useState([]);
  const [vaultPickerSearch, setVaultPickerSearch] = useState('');
  const [vaultPickerTags, setVaultPickerTags] = useState([]);
  const [vaultPickerView, setVaultPickerView] = useState('grid');
  const [vaultPickerPerPage, setVaultPickerPerPage] = useState(20);
  const [vaultPickerPage, setVaultPickerPage] = useState(1);
  const [vaultFolders, setVaultFolders] = useState([]);
  const [vaultUnfiledCount, setVaultUnfiledCount] = useState(0);
  const [vaultSortBy, setVaultSortBy] = useState('date_new');
  const [vaultMediaFilter, setVaultMediaFilter] = useState('all');
  const [vaultPreviewItem, setVaultPreviewItem] = useState(null);
  const [vaultSelection, setVaultSelection] = useState([]);
  const [showSelectionPreview, setShowSelectionPreview] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [bulkAddModal, setBulkAddModal] = useState(null); // { sessionId }
  // "Add from approved list": the account's Planner-approved subs
  const [approvedModal, setApprovedModal] = useState(null); // { sessionId, loading, subs }
  // Remote launch: which account browsers are online (extension check-ins)
  // and the latest command per session — /api/extension/status, every 10 s
  const [browsers, setBrowsers] = useState({ clients: {}, commands: {} });
  const [launchBusy, setLaunchBusy] = useState({});
  const cmdStatusRef = useRef({});
  const [approvedSearch, setApprovedSearch] = useState('');
  const [approvedSelected, setApprovedSelected] = useState([]);
  const [approvedAdding, setApprovedAdding] = useState(false);
  const [bulkAddText, setBulkAddText] = useState('');
  const [bulkAddLoading, setBulkAddLoading] = useState(false);
  const [vaultFolderSortBy, setVaultFolderSortBy] = useState('date_new');
  const [contentTags, setContentTags] = useState([]);
  const [vaultRestrictions, setVaultRestrictions] = useState({});
  // AI Caption presets
  const [aiPresets, setAiPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [showAiSettings, setShowAiSettings] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({ name: '', name_input: '', llm_prompt: '', temperature: 0.95 });
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [showAiHelp, setShowAiHelp] = useState(false);
  const [titlePicker, setTitlePicker] = useState(null);
  const [vaultSelectedFolderIds, setVaultSelectedFolderIds] = useState([]);
  const [showFolderSortDropdown, setShowFolderSortDropdown] = useState(false);
  const [editingPostUrl, setEditingPostUrl] = useState(null);
  const [editingPostUrlValue, setEditingPostUrlValue] = useState('');
  const [showPlanner, setShowPlanner] = useState(false);

  // Archive polls per session: { [sessionId]: { poll, timeout } } — cleared
  // when the archive is ready, on timeout, and when leaving the page.
  const archiveTimersRef = useRef({});
  const mountedRef = useRef(true);
  // Request counters so late responses can't overwrite newer state
  const pickSeqRef = useRef({});       // postId -> seq of the latest vault pick
  const [commentPlansKey, setCommentPlansKey] = useState(0); // Generate-Week wrote new comment plans
  // The account tab ('all' or an account id), remembered per model
  const accountTabKey = `scheduler_account_tab_${selectedCreator?.id || 'none'}`;
  // Read in the same render as a creator change (an effect showed the old tab,
  // i.e. all of the new creator's accounts, for one render)
  const [tabState, setTabState] = useState(() => ({ key: accountTabKey, tab: localStorage.getItem(accountTabKey) || 'all' }));
  const accountTab = tabState.key === accountTabKey ? tabState.tab : (localStorage.getItem(accountTabKey) || 'all');
  const setAccountTab = (tab) => setTabState({ key: accountTabKey, tab });
  const carouselSaveRef = useRef({});  // postId -> { busy, next } carousel order saves, one at a time
  const carouselDragRef = useRef(null); // { postId, from } while a carousel picture is dragged
  const [carouselDropAt, setCarouselDropAt] = useState(null); // { postId, index } drag hover
  const vaultOpenSeqRef = useRef(0);   // latest vault-picker open
  const completedSeqRef = useRef(0);   // latest completed-sessions load

  useEffect(() => {
    mountedRef.current = true;
    const timers = archiveTimersRef.current;
    return () => {
      mountedRef.current = false;
      for (const k of Object.keys(timers)) {
        clearInterval(timers[k].poll);
        clearTimeout(timers[k].timeout);
        delete timers[k];
      }
      hideActivity();
    };
  }, []);

  useEffect(() => {
    fetchAll();
    fetchContentTags();
  }, []);

  const fetchAll = async () => {
    try {
      const [sessionsRes, accountsRes, listsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/api/scheduler`),
        axios.get(`${API}/api/accounts`),
        axios.get(`${API}/api/posting-lists`),
        axios.get(`${API}/api/tags`)
      ]);
      setSessions(sessionsRes.data);
      setAccounts(accountsRes.data);
      setPostingLists(listsRes.data);
      setTags(tagsRes.data);
      return sessionsRes.data;
    } catch (err) {
      notifyError(err, 'Could not load the scheduler');
      return null;
    }
  };

  const fetchCompletedSessions = async () => {
    const seq = ++completedSeqRef.current;
    try {
      let url = `${API}/api/scheduler?status=posted&days=7`;
      if (creatorAccountIds && creatorAccountIds.length > 0) {
        url += `&account_ids=${creatorAccountIds.join(',')}`;
      }
      const res = await axios.get(url);
      if (seq !== completedSeqRef.current) return; // creator changed meanwhile
      setSessions(prev => {
        const active = prev.filter(s => s.status !== 'posted');
        return [...active, ...res.data];
      });
    } catch (err) {
      if (seq === completedSeqRef.current) notifyError(err, 'Could not load completed sessions');
    }
  };

  const fetchBrowsers = useCallback(async (accountIds) => {
    if (!accountIds.length) return;
    try {
      const res = await axios.get(`${API}/api/extension/status`, { params: { account_ids: accountIds.join(',') } });
      const data = res.data || { clients: {}, commands: {} };
      // a command that just finished → reload sessions (status, posts)
      let finished = false;
      for (const [k, c] of Object.entries(data.commands || {})) {
        const was = cmdStatusRef.current[k];
        if (was && was !== c.status && ['done', 'failed'].includes(c.status)) finished = true;
        cmdStatusRef.current[k] = c.status;
      }
      setBrowsers(data);
      if (finished) fetchSessions();
    } catch (err) { /* status is best-effort; the next poll retries */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A phone account (Accounts → Runs on): its work goes to the phone fleet
  const isPhone = (session) => session.accounts?.executor_type === 'fleet' || browsers.executors?.[session.account_id]?.executor_type === 'fleet';

  const sendBrowserCommand = async (session, type) => {
    const acc = session.accounts?.reddit_username ? `u/${session.accounts.reddit_username}` : 'this account';
    const phone = isPhone(session);
    if (type === 'start') {
      const left = (session.scheduled_posts || []).filter(p => !p.post_url).length;
      if (!window.confirm(phone
        ? `Send ${left} post${left === 1 ? '' : 's'} of ${acc} to the phone fleet?\n\nThe fleet runs it when the account's phone is free; the line below shows what it reports.`
        : `Start posting ${left} post${left === 1 ? '' : 's'} on autopilot in ${acc}'s browser?\n\nIt fills, posts and moves on by itself; blocked posts are skipped with their reason.`)) return;
    }
    setLaunchBusy(b => ({ ...b, [session.id]: true }));
    try {
      await axios.post(`${API}/api/extension/commands`, { account_id: session.account_id, session_id: session.id, type, options: type === 'start' ? { autopilot: true } : {} });
      notify(phone ? (type === 'start' ? `Sent to the phone fleet for ${acc}` : `Asked the phone to stop ${acc}'s session`)
        : type === 'start' ? `Sent to ${acc}'s browser — it starts within ~30 s` : `Stop sent to ${acc}'s browser`, 'success');
      await fetchBrowsers([session.account_id]);
    } catch (err) { notifyError(err, type === 'start' ? 'Could not launch' : 'Could not stop'); }
    setLaunchBusy(b => ({ ...b, [session.id]: false }));
  };

  const cancelBrowserCommand = async (cmd) => {
    try { await axios.post(`${API}/api/extension/commands/${cmd.id}/cancel`); await fetchBrowsers([cmd.account_id]); }
    catch (err) { notifyError(err, cmd.status === 'running' ? 'Could not stop' : 'Could not cancel'); }
  };

  useEffect(() => {
    const ids = [...new Set((sessions || []).filter(x => x.status !== 'posted').map(x => x.account_id).filter(Boolean))];
    if (!ids.length) return;
    fetchBrowsers(ids);
    const t = setInterval(() => { if (!document.hidden) fetchBrowsers(ids); }, 10000);
    return () => clearInterval(t);
  }, [sessions, fetchBrowsers]);

  const fetchSessions = async () => {
    try {
      const sessionsRes = await axios.get(`${API}/api/scheduler`);
      setSessions(prev => {
        // Keep completed sessions that were already loaded, update active ones
        const completed = prev.filter(s => s.status === 'posted');
        return [...sessionsRes.data, ...completed];
      });
      return sessionsRes.data;
    } catch (err) {
      notifyError(err, 'Could not refresh sessions');
      return null;
    }
  };

  const fetchContentTags = async () => {
    try {
      const res = await axios.get(`${API}/api/vault/content-tags`);
      setContentTags(res.data);
    } catch (err) {
      notifyError(err, 'Could not load content tags');
    }
  };



  const fetchVaultForSubreddit = async (accountId, subredditId) => {
    const key = `${accountId}-${subredditId}`;
    try {
      const creatorId = selectedCreator?.id;
      if (!creatorId) return;
      const res = await axios.get(`${API}/api/vault/creator/${creatorId}`);
      setVaultData(prev => ({ ...prev, [key]: res.data }));
    } catch (err) {
      notifyError(err, 'Could not load the vault');
    }
  };

  // Shallow-patch one post of one session in local state
  const patchPost = (sessionId, postId, patch) => {
    setSessions(prev => prev.map(s => s.id !== sessionId ? s : {
      ...s,
      scheduled_posts: (s.scheduled_posts || []).map(p =>
        p.id === postId ? { ...p, ...(typeof patch === 'function' ? patch(p) : patch) } : p)
    }));
  };

  const pickFromVault = async (sessionId, postId, vaultItems) => {
    const items = (Array.isArray(vaultItems) ? vaultItems : [vaultItems]).filter(Boolean);
    if (items.length === 0) return;
    const primary = items[0];
    const isCarousel = items.length > 1;

    const session = sessions.find(s => s.id === sessionId);
    const post = session?.scheduled_posts?.find(p => p.id === postId);
    if (!session || !post) return;
    const previousVaultId = post.vault_item_id;
    const previousCarouselItems = post.carousel_items || [];
    const hadCarouselRows = post.content_type === 'carousel' || previousCarouselItems.length > 0;

    // Only the latest pick for this post may roll back / resync it
    const seq = (pickSeqRef.current[postId] || 0) + 1;
    pickSeqRef.current[postId] = seq;
    const isLatest = () => pickSeqRef.current[postId] === seq;

    const carouselItems = isCarousel ? items.map((v, i) => ({
      vault_item_id: v.id,
      content_url: v.file_url,
      thumbnail_url: v.thumbnail_url,
      position: i
    })) : [];
    // An EMPTY title starts from the vault label when it names a character
    // (the VA turns "Fischl from Genshin Impact" into "Fischl [Genshin
    // Impact]"); a set label ("Christmas set") is no title — the same rule as
    // auto-fill (labelTitle in backend/routes/scheduler.js). A title already
    // there is never replaced. The local title is never re-sent — it could
    // revert what the VA just typed.
    const characterLabel = items.map(v => (v.title_suggestion || '').trim()).find(l => /^(.+?)\s+from\s+(.+)$/i.test(l));
    const newTitle = !(post.title || '').trim() && characterLabel ? characterLabel : null;
    const content = {
      content_url: primary.file_url,
      thumbnail_url: primary.thumbnail_url || null,
      content_type: isCarousel ? 'carousel' : 'file',
      vault_item_id: primary.id
    };
    const snapshot = {
      content_url: post.content_url, thumbnail_url: post.thumbnail_url, content_type: post.content_type,
      vault_item_id: post.vault_item_id, carousel_items: post.carousel_items
    };

    // Optimistic update
    patchPost(sessionId, postId, p => ({
      ...content,
      carousel_items: carouselItems,
      ...(newTitle ? { title: newTitle, _titleKey: Date.now() } : {})
    }));
    setVaultModal(null);
    setVaultSelection([]);

    let serverTouched = false;
    try {
      // Carousel → single: drop the old carousel rows first (the server
      // releases their vault items). Singles never send carousel_items, so
      // the server doesn't re-draft them as a one-item carousel.
      if (hadCarouselRows && !isCarousel) {
        await axios.delete(`${API}/api/scheduler/${sessionId}/posts/${postId}/carousel`);
        serverTouched = true;
      }
      const body = { ...content };
      if (newTitle) body.title = newTitle;
      if (isCarousel) body.carousel_items = carouselItems;
      const { data: saved } = await axios.put(`${API}/api/scheduler/${sessionId}/posts/${postId}`, body);
      serverTouched = true;

      const calls = items.map(v => axios.put(`${API}/api/vault/${v.id}/status`, {
        status: 'drafted',
        used_in_session_id: sessionId,
        account_id: session.account_id,
        subreddit_id: post.subreddit_id
      }));
      if (previousVaultId && !items.some(v => v.id === previousVaultId)) {
        calls.push(axios.put(`${API}/api/vault/${previousVaultId}/status`, { status: 'available' }));
      }
      for (const prev of previousCarouselItems) {
        if (prev.vault_item_id && prev.vault_item_id !== previousVaultId && !items.some(v => v.id === prev.vault_item_id)) {
          calls.push(axios.put(`${API}/api/vault/${prev.vault_item_id}/status`, { status: 'available' }));
        }
      }
      await Promise.all(calls);

      // New content → the server re-picked the flair (unless chosen by hand)
      if (isLatest() && saved && saved.flair_source !== 'manual') {
        patchPost(sessionId, postId, {
          flair_id: saved.flair_id ?? null, flair_text: saved.flair_text ?? null,
          flair_source: saved.flair_source ?? null, flair_why: saved.flair_why ?? null, flair_auto: null
        });
      }
    } catch (err) {
      notifyError(err, 'Could not use this vault item');
      if (!isLatest()) return; // a newer pick owns this post now
      if (!serverTouched) {
        // Nothing was saved → put the post back exactly as it was
        patchPost(sessionId, postId, p => ({
          ...snapshot,
          ...(newTitle ? { title: post.title, _titleKey: Date.now() } : {})
        }));
      } else {
        // Partly saved → take the server's version
        fetchSessions();
      }
    }
  };

  const saveRules = async () => {
    if (!rulesModal) return;
    try {
      await axios.put(`${API}/api/subreddits/${rulesModal.id}`, { rules: rulesValue });
      setEditingRules(false);
      setRulesModal({ ...rulesModal, rules: rulesValue });
      fetchAll();
    } catch (err) {
      notifyError(err, 'Could not save rules');
    }
  };

  const saveTitleRequirements = async (subredditId) => {
    try {
      await axios.put(`${API}/api/subreddits/${subredditId}`, { title_requirements: titleReqValue });
      setEditingTitleReq(null);
      fetchSessions();
    } catch (err) {
      notifyError(err, 'Could not save title requirements');
    }
  };

  const createSession = async () => {
    if (!form.account_id || !form.scheduled_date) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/scheduler`, {
        account_id: form.account_id,
        scheduled_date: form.scheduled_date,
        name: form.name,
        spoof_preset: form.spoof_preset,
        spoof_preset_gif: form.spoof_preset_gif,
        include_profile_post: form.include_profile_post
      });
      setForm({ account_id: '', scheduled_date: '', name: '', spoof_preset: 'medium', spoof_preset_gif: 'medium', include_profile_post: true });
      setShowForm(false);
      fetchSessions();
    } catch (err) {
      notifyError(err, 'Could not create session');
    }
    setLoading(false);
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this session and all its posts?')) return;
    try {
      await axios.delete(`${API}/api/scheduler/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      notifyError(err, 'Could not delete session');
    }
  };

  // Auto-fill content (beta): fill every empty slot with a vault item, then
  // reload so the VA can audit + title. Reports what it couldn't fill.
  const autofillContent = async (sessionId, gifPct = 30, carouselCount = 'auto') => {
    setAutofillModal(null);
    setAutofilling(sessionId);
    try {
      const res = await axios.post(`${API}/api/scheduler/${sessionId}/autofill-content`, { gif_ratio: gifPct / 100, carousel_count: carouselCount });
      const d = res.data;
      await fetchSessions();
      if (!d.total_slots) { alert('No empty slots to fill — every post already has content.'); }
      else {
        let msg = `✨ Filled ${d.filled}/${d.total_slots} slots · ${d.singles} single · ${d.carousels} carousel · ${d.gif_pct}% GIF (target ${d.gif_target_pct}%)`;
        if (d.unfilled > 0) msg += `\n\n${d.unfilled} left empty — no matching content (audit these):\n` +
          (d.unfilled_detail || []).map(u => `• r/${u.subreddit}: ${u.reason}`).join('\n');
        alert(msg);
      }
    } catch (err) {
      alert('Auto-fill failed: ' + (err.response?.data?.error || err.message));
    }
    setAutofilling(null);
  };

  const updateSessionStatus = async (id, status) => {
    // On Mark Ready: warn about posts with no title or no content so they
    // aren't missed, and expand the session so the highlighted cards are visible
    if (status === 'ready') {
      const session = sessions.find(s => s.id === id);
      const incomplete = (session?.scheduled_posts || []).filter(p =>
        !p.is_profile_post && (!p.title || !p.content_url)
      );
      const otherAccount = (session?.scheduled_posts || []).filter(p => !p.is_profile_post && closedWhy(session.account_id, p.subreddit_id));
      if (incomplete.length > 0 || otherAccount.length > 0) {
        setExpandedSession(id);
        const listOf = (posts) => posts.slice(0, 10).map(p => p.subreddits?.name || getSubredditName(p.subreddit_id)).join('\n• ') + (posts.length > 10 ? '\n• …' : '');
        const parts = [];
        if (incomplete.length) parts.push(`⚠ ${incomplete.length} post${incomplete.length !== 1 ? 's are' : ' is'} missing a title or content (yellow in the session):\n\n• ${listOf(incomplete)}`);
        if (otherAccount.length) parts.push(`👥 ${otherAccount.length} post${otherAccount.length !== 1 ? 's go' : ' goes'} to subs another account of this model uses (red in the session) — remove ${otherAccount.length !== 1 ? 'them' : 'it'}:\n\n• ${listOf(otherAccount)}`);
        const proceed = window.confirm(`${parts.join('\n\n')}\n\nMark ready anyway?`);
        if (!proceed) return;
      }
    }
    try {
      await axios.put(`${API}/api/scheduler/${id}/status`, { status });
      setSessions(prev => prev.map(s => {
        if (s.id !== id) return s;
        const updates = { status };
        if (status === 'draft') {
          updates.archive_url = null;
          updates.archive_path = null;
        }
        return { ...s, ...updates };
      }));
    } catch (err) {
      notifyError(err, 'Could not change session status');
    }
  };

  const updateSessionDate = async (id, newDate) => {
    try {
      const { data } = await axios.put(`${API}/api/scheduler/${id}`, { scheduled_date: newDate });
      setSessions(prev => prev.map(s => s.id === id ? { ...s, scheduled_date: newDate } : s));
      // Subs whose own rule ("1 post per 24h") this date now breaks
      const warn = data?.spacing_warnings || [];
      if (warn.length) notify(`On ${newDate} these subs break their own posting rule: ` +
        warn.map(w => `r/${String(w.subreddit || '').replace(/^r\//, '')} (1 per ${w.rule_hours}h; ${w.conflict})`).join(', ') +
        ' — move them to another day or remove them from this session.', 'error', 15000);
    } catch (err) {
      notifyError(err, 'Could not change session date');
    }
  };

  const updateSessionName = async (id, name) => {
    try {
      await axios.put(`${API}/api/scheduler/${id}`, { name });
      setEditingSessionName(null);
      setSessions(prev => prev.map(s => s.id === id ? { ...s, name } : s));
    } catch (err) {
      notifyError(err, 'Could not rename session');
    }
  };

  // "N skipped: another account of this model uses them" from a bulk add's reply
  const skippedOtherAccount = (reply) => {
    const n = reply?.skipped_other_account?.length || 0;
    return n ? `${n} skipped: another account of this model uses ${n === 1 ? 'it' : 'them'}` : null;
  };

  const addPostFromList = async (sessionId, listId) => {
    const list = postingLists.find(l => String(l.id) === String(listId));
    if (!list) return;
    const subredditIds = (list.posting_list_subreddits || []).map(s => s.subreddit_id);
    if (subredditIds.length === 0) return;

    try {
      const bulkRes = await axios.post(`${API}/api/scheduler/${sessionId}/posts/bulk`, { subreddit_ids: subredditIds });
      const skippedDup = bulkRes.data?.skipped_duplicates || 0;
      const skippedBan = bulkRes.data?.skipped_banned || 0;
      const skippedOther = skippedOtherAccount(bulkRes.data);
      if (skippedDup > 0 || skippedBan > 0 || skippedOther) {
        const parts = [];
        if (skippedDup > 0) parts.push(`${skippedDup} already in this session`);
        if (skippedBan > 0) parts.push(`${skippedBan} banned for this account`);
        if (skippedOther) parts.push(skippedOther);
        alert(`⚠ Skipped: ${parts.join(', ')}`);
      }

      try {
        const intervalRes = await axios.post(`${API}/api/subreddits/check-intervals`, {
          subreddit_ids: subredditIds,
          account_id: sessions.find(s => s.id === sessionId)?.account_id
        });
        setIntervalStatus(prev => ({ ...prev, ...intervalRes.data }));
      } catch (err) { notifyError(err, 'Could not check posting intervals'); }

      const session = sessions.find(s => s.id === sessionId);
      const currentIds = session?.posting_list_ids || [];
      if (!currentIds.includes(list.id)) {
        await axios.put(`${API}/api/scheduler/${sessionId}/lists`, { posting_list_ids: [...currentIds, list.id] });
      }
      fetchSessions();
    } catch (err) {
      notifyError(err, 'Could not add posts from list');
    }
  };

  // The account's approved subs from the Planner (same list Generate-Week
  // draws from), so a session can be filled by hand from vetted subs.
  const openApprovedModal = async (session) => {
    setApprovedSearch('');
    setApprovedSelected([]);
    setApprovedModal({ sessionId: session.id, loading: true, subs: [] });
    try {
      const res = await axios.get(`${API}/api/discovery/planner/${session.account_id}`);
      // + the approved subs another account of the model uses: shown, never pickable
      const subs = [...(res.data.approved || []), ...(res.data.approved_closed || []).map(s => ({ ...s, closed: true }))];
      setApprovedModal(m => (m && m.sessionId === session.id) ? { ...m, loading: false, subs } : m);
    } catch (err) {
      notifyError(err, 'Could not load the approved list');
      setApprovedModal(null);
    }
  };

  const addApprovedToSession = async () => {
    const sessionId = approvedModal?.sessionId;
    if (!sessionId || approvedSelected.length === 0) return;
    setApprovedAdding(true);
    try {
      const bulkRes = await axios.post(`${API}/api/scheduler/${sessionId}/posts/bulk`, { subreddit_ids: approvedSelected });
      try {
        const intervalRes = await axios.post(`${API}/api/subreddits/check-intervals`, {
          subreddit_ids: approvedSelected,
          account_id: sessions.find(s => s.id === sessionId)?.account_id
        });
        setIntervalStatus(prev => ({ ...prev, ...intervalRes.data }));
      } catch (e) { notifyError(e, 'Could not check posting intervals'); }
      await fetchSessions();
      const added = bulkRes.data?.added?.length ?? approvedSelected.length;
      const parts = [`${added} approved sub${added !== 1 ? 's' : ''} added`];
      if (bulkRes.data?.skipped_duplicates > 0) parts.push(`${bulkRes.data.skipped_duplicates} already in this session`);
      if (bulkRes.data?.skipped_banned > 0) parts.push(`${bulkRes.data.skipped_banned} banned for this account`);
      if (skippedOtherAccount(bulkRes.data)) parts.push(skippedOtherAccount(bulkRes.data));
      notify(parts.join(' · '), 'success');
      setApprovedModal(null);
    } catch (err) {
      notifyError(err, 'Could not add the approved subs');
    }
    setApprovedAdding(false);
  };

  const bulkAddToSession = async (sessionId) => {
    const names = bulkAddText.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
    if (names.length === 0) return;

    setBulkAddLoading(true);
    try {
      // 1. Ensure all subreddits exist in DB
      const ensureRes = await axios.post(`${API}/api/subreddits/bulk-ensure`, { names });
      const allSubs = ensureRes.data.subreddits;

      // Update local subreddits list
      setSubreddits(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const newSubs = allSubs.filter(s => !existingIds.has(s.id));
        return [...prev, ...newSubs].sort((a, b) => a.name.localeCompare(b.name));
      });

      // 2. Add all posts in one call
      const bulkRes = await axios.post(`${API}/api/scheduler/${sessionId}/posts/bulk`, {
        subreddit_ids: allSubs.map(s => s.id)
      });

      // 3. Check intervals
      try {
        const intervalRes = await axios.post(`${API}/api/subreddits/check-intervals`, {
          subreddit_ids: allSubs.map(s => s.id),
          account_id: sessions.find(s => s.id === sessionId)?.account_id
        });
        setIntervalStatus(prev => ({ ...prev, ...intervalRes.data }));
      } catch (e) { notifyError(e, 'Could not check posting intervals'); }

      await fetchSessions();

      const addedCount = bulkRes.data?.added?.length ?? allSubs.length;
      const parts = [];
      parts.push(`${addedCount} subreddit${addedCount !== 1 ? 's' : ''} added as posts`);
      if (bulkRes.data?.skipped_duplicates > 0) parts.push(`${bulkRes.data.skipped_duplicates} skipped (already in this session)`);
      if (bulkRes.data?.skipped_banned > 0) parts.push(`${bulkRes.data.skipped_banned} skipped (banned for this account)`);
      if (skippedOtherAccount(bulkRes.data)) parts.push(skippedOtherAccount(bulkRes.data));
      if (ensureRes.data.created > 0) parts.push(`${ensureRes.data.created} new subreddit${ensureRes.data.created !== 1 ? 's' : ''} created in database`);
      const existing = allSubs.length - ensureRes.data.created;
      if (existing > 0) parts.push(`${existing} already existed in database`);
      alert(`✅ ${parts.join('\n• ')}`);

      setBulkAddText('');
      setBulkAddModal(null);
    } catch (err) {
      notifyError(err, 'Bulk add failed');
    }
    setBulkAddLoading(false);
  };

  const addSinglePost = async (sessionId, subredditId) => {
    try {
      await axios.post(`${API}/api/scheduler/${sessionId}/posts`, {
        subreddit_id: subredditId,
        title: '',
        content_type: 'file',
        content_url: ''
      });
    } catch (err) {
      const data = err.response?.data || {};
      const subName = subreddits.find(s => s.id === subredditId)?.name || 'Subreddit';
      if (data.duplicate) { alert(`⚠ ${subName} is already in this session — skipped.`); return; }
      if (data.banned) { alert(`🚫 ${subName} is banned for this account — skipped.`); return; }
      if (data.other_account) { alert(`👥 ${subName}: ${data.error}. This account stays out of it — skipped.`); return; }
      alert('Error adding post: ' + (data.error || err.message));
      return;
    }
    try {
      const intervalRes = await axios.post(`${API}/api/subreddits/check-intervals`, {
        subreddit_ids: [subredditId],
        account_id: sessions.find(s => s.id === sessionId)?.account_id
      });
      setIntervalStatus(prev => ({ ...prev, ...intervalRes.data }));
    } catch (err) {
      notifyError(err, 'Could not check posting interval');
    }
    fetchSessions();
  };

  const deletePost = async (sessionId, postId) => {
    const session = sessions.find(s => s.id === sessionId);
    const post = session?.scheduled_posts?.find(p => p.id === postId);
    try {
      await axios.delete(`${API}/api/scheduler/${sessionId}/posts/${postId}`);
    } catch (err) {
      notifyError(err, 'Could not remove the post');
      return; // nothing freed — the post (and its vault item) stay as they were
    }
    // Update local state instead of refetching
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s;
      return {
        ...s,
        scheduled_posts: (s.scheduled_posts || []).filter(p => p.id !== postId)
      };
    }));
    // The server releases the item on delete; this only backs it up, and
    // only once the delete actually went through.
    if (post?.vault_item_id && post.content_type !== 'carousel') {
      axios.put(`${API}/api/vault/${post.vault_item_id}/status`, { status: 'available' }).catch(() => {});
    }
  };

  // Send ONLY the fields that change (never `...post`: a stale copy would
  // overwrite e.g. a title typed a moment ago). Returns true on success.
  const updatePost = async (sessionId, postId, updates) => {
    const session = sessions.find(s => s.id === sessionId);
    const post = session?.scheduled_posts?.find(p => p.id === postId);
    try {
      const { data: saved } = await axios.put(`${API}/api/scheduler/${sessionId}/posts/${postId}`, updates);
      // Content cleared → release the old item, now that the save went through
      if ((updates.content_url === '' || updates.content_url === null) && post?.vault_item_id) {
        axios.put(`${API}/api/vault/${post.vault_item_id}/status`, { status: 'available' }).catch(() => {});
      }
      // New content → the server re-picked the flair; take it (and drop the
      // stale preview) unless the VA chose one by hand.
      if (saved && (updates.vault_item_id !== undefined || updates.content_type !== undefined) && saved.flair_source !== 'manual') {
        updates = { ...updates, flair_id: saved.flair_id ?? null, flair_text: saved.flair_text ?? null,
          flair_source: saved.flair_source ?? null, flair_why: saved.flair_why ?? null, flair_auto: null };
      }
      patchPost(sessionId, postId, updates);
      return true;
    } catch (err) {
      notifyError(err, 'Could not save the post');
      return false;
    }
  };

  const clearCarousel = async (sessionId, postId) => {
    try {
      await axios.delete(`${API}/api/scheduler/${sessionId}/posts/${postId}/carousel`);
      return true;
    } catch (err) {
      notifyError(err, 'Could not clear the carousel');
      return false;
    }
  };

  const EMPTY_CONTENT = { content_url: '', thumbnail_url: '', vault_item_id: null, content_type: 'file', carousel_items: [] };

  // Remove ONE image from a carousel (not the whole thing). >=2 left → stays a
  // carousel; 1 left → collapses to a single post; 0 → cleared.
  const deleteCarouselItem = async (sessionId, post, index) => {
    const items = post.carousel_items || [];
    const remaining = items.filter((_, i) => i !== index).map((ci, i) => ({ ...ci, position: i }));
    if (remaining.length >= 2) {
      const primary = remaining[0];
      // saveCarouselItems replaces the rows and releases the removed item
      await updatePost(sessionId, post.id, {
        carousel_items: remaining, content_type: 'carousel',
        vault_item_id: primary.vault_item_id, content_url: primary.content_url, thumbnail_url: primary.thumbnail_url
      });
    } else if (remaining.length === 1) {
      const only = remaining[0];
      if (!(await clearCarousel(sessionId, post.id))) return;
      const ok = await updatePost(sessionId, post.id, {
        carousel_items: [], content_type: 'file',
        vault_item_id: only.vault_item_id, content_url: only.content_url, thumbnail_url: only.thumbnail_url
      });
      if (ok && only.vault_item_id) {
        await axios.put(`${API}/api/vault/${only.vault_item_id}/status`, { status: 'drafted', used_in_session_id: sessionId }).catch(() => {});
      }
    } else {
      if (!(await clearCarousel(sessionId, post.id))) return;
      await updatePost(sessionId, post.id, EMPTY_CONTENT);
    }
  };

  // Reorder a carousel (drag a picture, or its ◀ ▶): the extension posts the
  // pictures in this order and the first one is the gallery's cover, so it is
  // also the post's own content. Moved on screen at once; saves go out one at
  // a time with the newest order (overlapping saves each replace the rows and
  // could leave both orders behind). A failed save reloads the sessions.
  const moveCarouselItem = (sessionId, post, from, to) => {
    const items = post.carousel_items || [];
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const reordered = next.map((ci, i) => ({ ...ci, position: i }));
    const cover = reordered[0];
    // Every save carries the cover: a skipped (superseded) save may have
    // been the one that changed it
    const content = { vault_item_id: cover.vault_item_id, content_url: cover.content_url, thumbnail_url: cover.thumbnail_url };
    patchPost(sessionId, post.id, { carousel_items: reordered, ...content });

    const q = carouselSaveRef.current[post.id] || (carouselSaveRef.current[post.id] = { busy: false, next: null });
    q.next = { carousel_items: reordered, ...content };
    if (q.busy) return;
    q.busy = true;
    (async () => {
      while (q.next) {
        const body = q.next;
        q.next = null;
        try {
          const { data: saved } = await axios.put(`${API}/api/scheduler/${sessionId}/posts/${post.id}`, body);
          // the server re-picks the automatic flair from the cover
          if (!q.next && saved && saved.flair_source !== 'manual') {
            patchPost(sessionId, post.id, { flair_id: saved.flair_id ?? null, flair_text: saved.flair_text ?? null,
              flair_source: saved.flair_source ?? null, flair_why: saved.flair_why ?? null, flair_auto: null });
          }
        } catch (err) {
          notifyError(err, 'Could not save the new picture order');
          q.next = null;
          await fetchSessions();
        }
      }
      q.busy = false;
    })();
  };

  // Repick one slot — swap in a different matched item (avoids the current +
  // previously-suggested ones for that slot).
  const repickPost = async (sessionId, postId, index = null) => {
    setRepicking(postId);
    try {
      const res = await axios.post(`${API}/api/scheduler/${sessionId}/posts/${postId}/repick`, index !== null ? { index } : {});
      const d = res.data || {};
      if (d.repicked) {
        setSessions(prev => prev.map(s => s.id !== sessionId ? s : {
          ...s,
          scheduled_posts: (s.scheduled_posts || []).map(p => {
            if (p.id !== postId) return p;
            const next = {
              ...p, vault_item_id: d.vault_item_id, content_url: d.content_url,
              thumbnail_url: d.thumbnail_url, title: d.title ?? p.title,
              content_type: d.content_type || 'file', pick_reason: d.pick_reason || null,
              carousel_items: d.content_type === 'carousel' ? (d.carousel_items || []) : [],
              _titleKey: d.title != null ? Date.now() : p._titleKey
            };
            if (p.flair_source !== 'manual') Object.assign(next, {
              flair_id: d.flair?.flair_id ?? null, flair_text: d.flair?.flair_text ?? null,
              flair_source: d.flair?.flair_source ?? null, flair_why: d.flair?.flair_why ?? null, flair_auto: null
            });
            return next;
          })
        }));
      } else {
        alert(d.reason || 'No other content available to repick.');
      }
    } catch (err) {
      alert('Repick failed: ' + (err.response?.data?.error || err.message));
    }
    setRepicking(null);
  };

  // Remove from the lightbox / preview: one carousel image, or the whole
  // content of a single post (releases the vault item either way).
  const removeContent = async (sessionId, postId, index = null) => {
    const post = sessions.find(s => s.id === sessionId)?.scheduled_posts?.find(p => p.id === postId);
    if (!post) return;
    const carousel = post.carousel_items || [];
    if (index !== null && carousel.length > 1) {
      await deleteCarouselItem(sessionId, post, index);
    } else if (carousel.length > 1) {
      const ids = carousel.map(ci => ci.vault_item_id).filter(Boolean);
      // Server first: drops the rows and releases their items
      if (!(await clearCarousel(sessionId, post.id))) return;
      const ok = await updatePost(sessionId, post.id, EMPTY_CONTENT);
      if (ok) for (const vid of ids) axios.put(`${API}/api/vault/${vid}/status`, { status: 'available' }).catch(() => {});
    } else {
      // Single item: explicit empty carousel + 'file' so nothing is re-drafted
      // (an old one-item carousel row is cleared too).
      if (carousel.length === 1 && !(await clearCarousel(sessionId, post.id))) return;
      await updatePost(sessionId, post.id, EMPTY_CONTENT);
    }
  };

  // Re-roll every AUTO-PICKED slot in the session. Manual picks, carousels and
  // VA-written titles are left untouched.
  const repickAll = async (sessionId) => {
    if (!window.confirm('Re-roll the auto-picked content in this session?\n\nYour manually chosen content, carousels and any titles you wrote are kept.')) return;
    setRepickingAll(sessionId);
    try {
      const res = await axios.post(`${API}/api/scheduler/${sessionId}/repick-all`);
      const d = res.data || {};
      await fetchSessions();
      if (!d.considered) alert(d.message || 'Nothing to re-roll.');
      else {
        const failed = (d.results || []).filter(r => !r.repicked);
        let msg = `🎲 Re-rolled ${d.repicked}/${d.considered} auto-picked slots.`;
        if (failed.length) msg += `\n\n${failed.length} kept their current content (no alternative available):\n` +
          failed.map(f => `• r/${f.subreddit}`).join('\n');
        alert(msg);
      }
    } catch (err) {
      alert('Re-roll failed: ' + (err.response?.data?.error || err.message));
    }
    setRepickingAll(null);
  };

  const uploadFile = async (sessionId, postId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `${API}/api/scheduler/${sessionId}/posts/${postId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      fetchSessions();
    } catch (err) {
      notifyError(err, 'Upload failed');
    }
  };

  // ── Archive generation (queued on the server, polled until ready) ──
  const stopArchiveTimers = (sessionId) => {
    const t = archiveTimersRef.current[sessionId];
    if (t) {
      clearInterval(t.poll);
      clearTimeout(t.timeout);
      delete archiveTimersRef.current[sessionId];
    }
  };

  const generateArchive = async (session, regenerate = false) => {
    if (regenerate && !window.confirm('Regenerate archive with fresh spoofed content?')) return;
    stopArchiveTimers(session.id);
    const setArchive = (extra) => setSessions(prev => prev.map(s => s.id === session.id ? { ...s, ...extra } : s));
    setArchive(regenerate ? { archive_url: null, _archiveGenerating: true } : { _archiveGenerating: true });
    showActivity(regenerate ? 'Regenerating archive...' : 'Generating archive...');
    try {
      const { data } = await axios.post(`${API}/api/scheduler/${session.id}/generate-archive`);
      if (data.archive_url) {
        // Already existed
        setArchive({ archive_url: data.archive_url, _archiveGenerating: false });
        hideActivity();
      } else if (data.queued) {
        if (!mountedRef.current) { hideActivity(); return; }
        const poll = setInterval(async () => {
          try {
            const checkRes = await axios.get(`${API}/api/scheduler`);
            const updated = checkRes.data.find(s => s.id === session.id);
            if (updated?.archive_url && archiveTimersRef.current[session.id]?.poll === poll) {
              stopArchiveTimers(session.id); // also cancels the timeout → no false "timed out"
              hideActivity();
              setArchive({ archive_url: updated.archive_url, _archiveGenerating: false });
            }
          } catch (e) { /* keep polling */ }
        }, 3000);
        const timeout = setTimeout(() => {
          if (archiveTimersRef.current[session.id]?.timeout !== timeout) return;
          stopArchiveTimers(session.id);
          hideActivity();
          setArchive({ _archiveGenerating: false });
          notify('Archive generation timed out — try again', 'error');
        }, 300000);
        archiveTimersRef.current[session.id] = { poll, timeout };
      } else {
        setArchive({ _archiveGenerating: false });
        hideActivity();
      }
    } catch (err) {
      setArchive({ _archiveGenerating: false });
      hideActivity();
      notifyError(err, 'Error generating archive');
    }
  };

  const flagSessionRescrape = async (sessionId) => {
    try {
      const res = await axios.post(`${API}/api/stats/flag-rescrape`, { session_id: sessionId });
      alert(`🔄 ${res.data.flagged} posts flagged for re-scraping`);
      fetchCompletedSessions();
    } catch (err) {
      notifyError(err, 'Could not flag for re-scraping');
    }
  };

  const flagPostRescrape = async (sessionId, postId) => {
    try {
      await axios.post(`${API}/api/stats/flag-rescrape`, { post_id: postId });
      patchPost(sessionId, postId, { needs_scraping: true });
    } catch (err) {
      notifyError(err, 'Could not flag for re-scraping');
    }
  };

  const savePostUrl = async (sessionId, postId, url) => {
    const value = (url || '').trim() || null;
    try {
      await axios.put(`${API}/api/scheduler/${sessionId}/posts/${postId}`, { post_url: value });
      patchPost(sessionId, postId, { post_url: value });
      setEditingPostUrl(null);
    } catch (err) {
      notifyError(err, 'Could not save the post link');
    }
  };

  const addProfilePost = async (sessionId) => {
    try {
      await axios.post(`${API}/api/scheduler/${sessionId}/posts`, {
        is_profile_post: true,
        subreddit_id: null,
        title: '',
        content_type: 'file',
        content_url: ''
      });
      fetchSessions();
    } catch (err) {
      notifyError(err, 'Could not add the profile post');
    }
  };

  const applyPickedTitle = async (sessionId, postId, title) => {
    try {
      await axios.put(`${API}/api/scheduler/${sessionId}/posts/${postId}`, { title });
      patchPost(sessionId, postId, { title, _titleKey: Date.now() });
      setTitlePicker(null);
    } catch (err) {
      notifyError(err, 'Could not save the title');
    }
  };

  // Open the vault picker for one post. Folders + usage restrictions load in
  // the background; if the picker is reopened for another post meanwhile,
  // the older responses are ignored.
  const openVaultPicker = async (sess, post) => {
    const seq = ++vaultOpenSeqRef.current;
    fetchVaultForSubreddit(sess.account_id, post.subreddit_id);
    setVaultModal({ sessionId: sess.id, postId: post.id, subredditId: post.subreddit_id, accountId: sess.account_id });
    setVaultPickerSearch('');
    setVaultPickerTags([]);
    setVaultPickerPage(1);
    setVaultMediaFilter('all');
    setVaultSelection([]);
    setShowSelectionPreview(false);
    const creatorId = selectedCreator?.id;
    const [foldersRes, restrictRes] = await Promise.allSettled([
      axios.get(`${API}/api/vault/folders/${creatorId}`),
      axios.get(`${API}/api/vault/usage-restrictions/${creatorId}?account_id=${sess.account_id}&session_id=${sess.id}`)
    ]);
    if (seq !== vaultOpenSeqRef.current) return;
    if (foldersRes.status === 'fulfilled') {
      setVaultFolders(foldersRes.value.data.folders || []);
      setVaultUnfiledCount(foldersRes.value.data.unfiled_count || 0);
    } else {
      notifyError(foldersRes.reason, 'Could not load vault folders');
    }
    setVaultRestrictions(restrictRes.status === 'fulfilled' ? (restrictRes.value.data.restrictions || {}) : {});
  };

  const getAccountName = (id) => {
    const account = accounts.find(a => a.id === id);
    return account ? `${account.name} (u/${account.reddit_username})` : 'Unknown';
  };

  const getSubredditName = (id) => {
    const sub = subreddits.find(s => s.id === id);
    return sub ? sub.name : 'Unknown';
  };

  const getSubredditUrl = (name) => {
    const clean = name?.replace('r/', '') || '';
    return `https://www.reddit.com/r/${clean}`;
  };

  const getStatusColor = (status) => {
    if (status === 'ready') return '#22c55e';
    if (status === 'posted') return '#3b82f6';
    if (status === 'draft') return '#f59e0b';
    return '#aaaaaa';
  };

  // AI Presets
  
  const fetchAiPresets = async () => {
    if (!selectedCreator?.id) return;
    try {
      const res = await axios.get(`${API}/api/ai-presets/${selectedCreator.id}`);
      setAiPresets(res.data);
    } catch (err) {
      notifyError(err, 'Could not load AI presets');
    }
  };

  useEffect(() => {
    if (selectedCreator?.id) {
      fetchAiPresets();
      const savedPreset = localStorage.getItem('ai_preset_' + selectedCreator.id);
      if (savedPreset) setSelectedPresetId(savedPreset);
      else setSelectedPresetId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreator?.id]);

  const savePreset = async () => {
    if (!presetForm.name.trim()) return;
    try {
      if (editingPreset) {
        await axios.put(`${API}/api/ai-presets/${editingPreset.id}`, presetForm);
      } else {
        await axios.post(`${API}/api/ai-presets`, { ...presetForm, creator_id: selectedCreator?.id });
      }
      setEditingPreset(null);
      setPresetForm({ name: '', name_input: '', llm_prompt: '', temperature: 0.95 });
      fetchAiPresets();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving preset');
    }
  };

  const deletePreset = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try {
      await axios.delete(`${API}/api/ai-presets/${id}`);
      if (selectedPresetId === id) {
        setSelectedPresetId('');
        // same key selectPreset() writes
        localStorage.removeItem('ai_preset_' + (selectedCreator?.id || 'default'));
      }
      fetchAiPresets();
    } catch (err) {
      notifyError(err, 'Could not delete preset');
    }
  };

  const selectPreset = (id) => {
    setSelectedPresetId(id);
    const key = 'ai_preset_' + (selectedCreator?.id || 'default');
    if (id) {
      localStorage.setItem(key, id);
    } else {
      localStorage.removeItem(key);
    }
  };
  

  const handleTitleSave = useCallback(async (sessionId, postId, title) => {
    try {
      await axios.put(`${API}/api/scheduler/${sessionId}/posts/${postId}`, { title });
      setSessions(prev => prev.map(s => {
        if (s.id !== sessionId) return s;
        return {
          ...s,
          scheduled_posts: (s.scheduled_posts || []).map(p =>
            p.id === postId ? { ...p, title } : p
          )
        };
      }));
    } catch (err) {
      notifyError(err, 'Could not save the title');
    }
  }, []);

  // Flair dropdown: '' = back to automatic; anything else = the VA's choice
  // (kept as manual, and remembered for this model in this sub).
  const handleFlairChange = useCallback(async (sessionId, post, flairId) => {
    const flairs = post.subreddits?.flairs || [];
    const f = flairs.find(x => x.id === flairId);
    const body = flairId
      ? { flair_id: flairId, flair_text: f?.label || '', flair_source: 'manual' }
      : { flair_id: null, flair_text: null, flair_source: null };
    try {
      const { data } = await axios.put(`${API}/api/scheduler/${sessionId}/posts/${post.id}`, body);
      const next = { flair_id: data?.flair_id ?? body.flair_id, flair_text: data?.flair_text ?? body.flair_text,
        flair_source: data?.flair_source ?? body.flair_source, flair_why: data?.flair_why ?? null, flair_auto: null };
      setSessions(prev => prev.map(s => s.id !== sessionId ? s : {
        ...s, scheduled_posts: (s.scheduled_posts || []).map(p => p.id === post.id ? { ...p, ...next } : p)
      }));
    } catch (err) {
      notifyError(err, 'Could not save the flair');
    }
  }, []);

  const getMinDate = () => new Date().toISOString().split('T')[0];
  const getMaxDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  };

  const subPickerTagNames = tags.map(t => t.name).sort();

  const filteredSubreddits = subreddits.filter(sub => {
    const matchSearch = sub.name.toLowerCase().includes(subPickerSearch.toLowerCase());
    const matchTags = subPickerTags.length === 0 || subPickerTags.every(t => (sub.tags || []).includes(t));
    return matchSearch && matchTags;
  }).sort((a, b) => a.name.localeCompare(b.name));

  // Creator changed → reload the completed list for the new creator's accounts
  useEffect(() => {
    if (showCompleted) fetchCompletedSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorAccountIds]);

  // Account tabs: one account's sessions, Planner and comments at a time (a
  // remembered account that left the model falls back to All)
  const modelAccounts = creatorAccountIds ? accounts.filter(a => creatorAccountIds.includes(a.id)) : accounts;
  const showAccountTabs = !!creatorAccountIds && modelAccounts.length > 1;
  const tabAccount = showAccountTabs ? modelAccounts.find(a => a.id === accountTab) || null : null;
  const shownAccounts = tabAccount ? [tabAccount] : modelAccounts;
  const karmaPosting = useKarmaPostingAccounts(shownAccounts.map(a => a.id));
  const fillFromKarmaIdea = (sessionId, postId, saved) => patchPost(sessionId, postId, {
    content_url: saved.content_url, thumbnail_url: saved.thumbnail_url, content_type: saved.content_type,
    vault_item_id: null, title: saved.title, _titleKey: Date.now()
  });
  const pickAccountTab = (id) => { setAccountTab(id); localStorage.setItem(accountTabKey, id); };

  const shownAccountIds = tabAccount ? [tabAccount.id] : creatorAccountIds;
  const filteredSessions = shownAccountIds
    ? sessions.filter(s => shownAccountIds.includes(s.account_id))
    : sessions;

  const sessionsByDate = filteredSessions.reduce((acc, session) => {
    const date = session.scheduled_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {});

  // A phone account's session line: its device, and what the fleet reports
  // about the session's job (sent → waiting with position and reason → running)
  const renderPhoneLine = (session) => {
    const ex = browsers.executors?.[session.account_id] || {};
    const cmd = browsers.commands?.[session.id];
    const ago = (t) => { const m = Math.round((Date.now() - Date.parse(t)) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
    const where = `📱 Runs on a phone · ${ex.device_serial || session.accounts?.fleet_device_serial || '?'} · profile ${ex.profile_serial ?? session.accounts?.fleet_profile_serial ?? '?'}`;
    let line = null, color = '#777';
    if (cmd && cmd.type === 'start') {
      const r = cmd.result || {};
      if (cmd.status === 'queued' && cmd.fleet_state === 'waiting') { line = `⏳ Waiting at the fleet${cmd.fleet_position ? ` · #${cmd.fleet_position} in line` : ''}${cmd.fleet_reason ? ` · ${cmd.fleet_reason}` : ''}`; color = '#f59e0b'; }
      else if (cmd.status === 'queued') { line = `📨 Sent to the fleet ${ago(cmd.created_at)}`; color = '#f59e0b'; }
      else if (cmd.status === 'running') { line = cmd.cancel_requested ? '■ Stopping…' : '📱 Running on the phone'; color = '#22c55e'; }
      else if (cmd.status === 'done') {
        const c = r.counts || {};
        line = `✓ Finished ${ago(cmd.finished_at || cmd.created_at)}${r.counts ? `: ${c.published || 0} published${c.removed ? `, ${c.removed} removed` : ''}${c.failed ? `, ${c.failed} failed` : ''}${c.skipped ? `, ${c.skipped} skipped` : ''}` : ''}`;
        color = '#22c55e';
      } else if (cmd.status === 'cancelled') { line = `✕ ${r.error || 'Cancelled'}`; }
      else if (['failed', 'expired'].includes(cmd.status)) { line = `⚠ ${r.error || 'Failed'}`; color = '#ef4444'; }
    }
    const open = cmd && cmd.type === 'start' && ['queued', 'running'].includes(cmd.status) && !cmd.cancel_requested;
    return (
      <div style={{ fontSize: '11px', marginTop: '4px', color: '#777' }}>
        <span>{where}</span>
        {line && <span style={{ color, marginLeft: '8px' }}>{line}</span>}
        {open && (
          <button onClick={() => cancelBrowserCommand(cmd)} style={{ marginLeft: '8px', background: 'transparent', border: 'none', color: '#777', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>{cmd.status === 'running' ? 'stop' : 'cancel'}</button>
        )}
      </div>
    );
  };

  // Browser + remote-launch line of a session: is the account's browser
  // online, and what the latest command for this session is doing.
  const renderBrowserLine = (session) => {
    if (isPhone(session)) return renderPhoneLine(session);
    const cl = browsers.clients?.[session.account_id];
    const cmd = browsers.commands?.[session.id];
    const ago = (t) => { const m = Math.round((Date.now() - Date.parse(t)) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
    const st = cl?.state || {};
    const c = st.counts || {};
    let line = null, color = '#777';
    if (cmd && cmd.status === 'queued') { line = `⏳ Sent to the browser ${ago(cmd.created_at)} — waiting for it to pick it up`; color = '#f59e0b'; }
    else if (cl?.online && st.posting && st.session_id === session.id) {
      line = `🤖 Posting${st.current ? ' ' + st.current : ''} · ${st.left ?? 0} left · ${c.published || 0} published${c.removed ? `, ${c.removed} removed` : ''}${c.failed ? `, ${c.failed} failed` : ''}${c.skipped ? `, ${c.skipped} skipped` : ''}`;
      color = '#22c55e';
    } else if (cmd && cmd.status === 'running' && cl?.online && st.wrapping_up) { line = '🤖 Wrapping up (summary, retry session)…'; color = '#22c55e'; }
    else if (cmd && cmd.status === 'done' && cmd.type === 'start') {
      const r = cmd.result?.counts || {};
      line = `✓ Finished ${ago(cmd.finished_at || cmd.created_at)}: ${r.published || 0} published${r.removed ? `, ${r.removed} removed` : ''}${r.failed ? `, ${r.failed} failed` : ''}${r.skipped ? `, ${r.skipped} skipped` : ''}`;
      color = '#22c55e';
    } else if (cmd && ['failed', 'expired'].includes(cmd.status)) { line = `⚠ ${cmd.result?.error || (cmd.status === 'expired' ? 'No browser picked it up' : 'Failed')}`; color = '#ef4444'; }
    const dot = cl?.online ? `🟢 Browser online${cl.version ? ` · v${cl.version}` : ''}${st.posting && st.session_id !== session.id ? ' · posting another session' : ''}`
      : cl ? `⚫ Browser offline · seen ${ago(cl.last_seen_at)}` : '⚫ No browser has checked in for this account';
    return (
      <div style={{ fontSize: '11px', marginTop: '4px', color: '#777' }}>
        <span>{dot}</span>
        {line && <span style={{ color, marginLeft: '8px' }}>{line}</span>}
        {cmd && cmd.status === 'queued' && (
          <button onClick={() => cancelBrowserCommand(cmd)} style={{ marginLeft: '8px', background: 'transparent', border: 'none', color: '#777', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>cancel</button>
        )}
      </div>
    );
  };

  const renderSession = (session, isCompleted = false) => (
    <div className="card" key={session.id} style={isCompleted ? { opacity: 0.7 } : {}}>
      <div className="list-header">
        <div>
          {isCompleted ? (
            <strong>{session.name || getAccountName(session.account_id)}</strong>
          ) : editingSessionName === session.id ? (
            <div className="form-row" style={{ marginBottom: '4px' }}>
              <input
                value={editingSessionNameValue}
                onChange={e => setEditingSessionNameValue(e.target.value)}
                style={{ fontSize: '14px', width: '100%' }}
              />
              <button onClick={() => updateSessionName(session.id, editingSessionNameValue)}>Save</button>
              <button className="btn-secondary" onClick={() => setEditingSessionName(null)}>Cancel</button>
            </div>
          ) : (
            <strong
              onClick={() => { setEditingSessionName(session.id); setEditingSessionNameValue(session.name || getAccountName(session.account_id)); }}
              style={{ cursor: 'pointer', borderBottom: '1px dashed #555', paddingBottom: '1px' }}
              title="Click to rename"
            >
              {session.name || getAccountName(session.account_id)} ✏️
            </strong>
          )}
          {(session.posting_list_ids || []).length > 0 && (
            <div style={{ fontSize: '12px', color: '#aaaaaa', marginTop: '2px' }}>
              {'\uD83D\uDCCB'} {(session.posting_list_ids || []).map(id => {
                const list = postingLists.find(l => l.id === id);
                return list ? list.name : null;
              }).filter(Boolean).join(', ')}
            </div>
          )}
          <span className="sub"> · {(session.scheduled_posts || []).length} posts</span>
          {(() => {
            const bad = (session.scheduled_posts || []).filter(p => postFitIssue(p)).length;
            return bad > 0 ? <span style={{ marginLeft: '8px', fontSize: '11px', color: '#ef4444', fontWeight: 600 }}
              title="Posts whose content this sub can't take (galleries off, dead sub, not for creators…). See the red line on each post.">⛔ {bad} can't post as planned</span> : null;
          })()}
          <span className="status-badge" style={{ background: getStatusColor(session.status) }}>
            {session.status === 'posted' ? 'Posting Completed' : session.status}
          </span>
          {!isCompleted && renderBrowserLine(session)}
        </div>
        <div className="list-item-actions">
          {isCompleted && (
            <>
              <button
                className="btn-secondary"
                onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
              >
                {expandedSession === session.id ? 'Collapse' : 'View Posts'}
              </button>
              {expandedSession === session.id && (
                <button
                  className="btn-secondary"
                  style={{ fontSize: '11px', color: '#8b5cf6', borderColor: '#7c3aed' }}
                  onClick={() => flagSessionRescrape(session.id)}
                >🔄 Re-scrape session</button>
              )}
            </>
          )}

          {!isCompleted && session.status === 'ready' && !session.archive_url && (
            <button
              className="btn-secondary"
              disabled={session._archiveGenerating}
              onClick={() => generateArchive(session)}
              style={session._archiveGenerating ? { opacity: 0.6, cursor: 'wait' } : {}}
            >
              {session._archiveGenerating ? '⏳ Generating...' : '📦 Generate Archive'}
            </button>
          )}
          {!isCompleted && session.status === 'ready' && session.archive_url && (
            <button
              className="btn-secondary"
              onClick={async () => {
                try {
                  const res = await fetch(session.archive_url);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${session.name || 'archive'}.zip`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (e) {
                  window.open(session.archive_url, '_blank');
                }
              }}
            >
              ⬇️ Download Archive
            </button>
          )}
          {!isCompleted && session.status === 'ready' && session.archive_url && (
            <button
              className="btn-secondary"
              disabled={session._archiveGenerating}
              onClick={() => generateArchive(session, true)}
              style={{ fontSize: '11px', color: '#aaaaaa', borderColor: '#444' }}
            >
              {session._archiveGenerating ? '⏳ Regenerating...' : '🔄 Regenerate'}
            </button>
          )}
          
          {!isCompleted && session.status === 'draft' && (
            <>
              <button className="btn-secondary" disabled={autofilling === session.id}
                title="Auto-fill empty slots with vault content (beta) — you then audit + title"
                onClick={() => setAutofillModal(session.id)}>
                {autofilling === session.id ? '⏳ Filling...' : '✨ Auto-fill content'}
              </button>
              <button className="btn-secondary" disabled={repickingAll === session.id}
                title="Re-roll auto-picked content (keeps your manual picks and titles)"
                onClick={() => repickAll(session.id)}>
                {repickingAll === session.id ? '⏳ Re-rolling...' : '🎲 Re-roll picks'}
              </button>
              <button className="btn-secondary" onClick={() => {
                const fresh = sessions.find(s => s.id === session.id);
                setPreviewSession(fresh || session);
              }}>Preview</button>
              <button className="btn-secondary" onClick={() => updateSessionStatus(session.id, 'ready')}>Mark Ready</button>
            </>
          )}
          {!isCompleted && session.status === 'ready' && isPhone(session) && (() => {
            const cmd = browsers.commands?.[session.id];
            const open = cmd && cmd.type === 'start' && ['queued', 'running'].includes(cmd.status);
            return <button className="btn-secondary" disabled={open || !!launchBusy[session.id]}
              title={open ? 'Already with the fleet — the line below shows what it reports (cancel / stop there)' : 'Send this session to the phone fleet: it runs when the account\'s phone is free'}
              style={open ? undefined : { color: '#22c55e', borderColor: '#166534' }}
              onClick={() => sendBrowserCommand(session, 'start')}>📱 Launch</button>;
          })()}
          {!isCompleted && session.status === 'ready' && !isPhone(session) && (() => {
            const cl = browsers.clients?.[session.account_id];
            const cmd = browsers.commands?.[session.id];
            const postingThis = cl?.online && cl.state?.posting && cl.state?.session_id === session.id;
            const waiting = cmd && cmd.status === 'queued';
            if (postingThis || (cmd && cmd.status === 'running' && cl?.online && cl.state?.posting)) {
              return <button className="btn-secondary" disabled={!!launchBusy[session.id]} style={{ color: '#ef4444', borderColor: '#7f1d1d' }}
                onClick={() => sendBrowserCommand(session, 'stop')}>■ Stop</button>;
            }
            const can = cl?.online && !cl.state?.posting && !waiting;
            return <button className="btn-secondary" disabled={!can || !!launchBusy[session.id]}
              title={!cl?.online ? "No browser with this account is online — open its Dolphin profile and log in to the extension" : cl.state?.posting ? 'That browser is busy posting another session' : waiting ? 'Already sent — waiting for the browser' : 'Post this session on autopilot in the account\'s browser'}
              style={can ? { color: '#22c55e', borderColor: '#166534' } : undefined}
              onClick={() => sendBrowserCommand(session, 'start')}>▶ Launch</button>;
          })()}
          {!isCompleted && session.status === 'ready' && (
            <button className="btn-secondary" onClick={() => updateSessionStatus(session.id, 'draft')}>Back to Draft</button>
          )}
          {!isCompleted && (
            <button
              className="btn-secondary"
              onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
            >
              {expandedSession === session.id ? 'Collapse' : 'Manage'}
            </button>
          )}
          <button className="btn-danger" onClick={() => deleteSession(session.id)}>Delete</button>
        </div>
      </div>

      {expandedSession === session.id && isCompleted && (
        <div className="expanded-list">
          {(session.scheduled_posts || []).length === 0 ? (
            <p className="empty">No posts in this session.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(session.scheduled_posts || []).slice().sort((a, b) => { if (a.is_profile_post && !b.is_profile_post) return -1; if (!a.is_profile_post && b.is_profile_post) return 1; return new Date(a.created_at) - new Date(b.created_at); }).map(post => (
                <div key={post.id} style={{
                  display: 'flex', gap: '12px', alignItems: 'center',
                  background: '#0f0f0f', borderRadius: '8px', padding: '10px 12px',
                  border: '1px solid #2a2a2a'
                }}>
                  {post.content_url ? (
                    post.post_url ? (
                      <a href={post.post_url} target="_blank" rel="noreferrer">
                        <img src={post.thumbnail_url || post.content_url} alt="" onError={(e) => { if (post.content_url && e.target.src !== post.content_url) e.target.src = post.content_url; else e.target.style.display = 'none'; }} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, cursor: 'pointer', border: '2px solid #ff4500' }} />
                      </a>
                    ) : (
                      <img src={post.thumbnail_url || post.content_url} alt="" onError={(e) => { if (post.content_url && e.target.src !== post.content_url) e.target.src = post.content_url; else e.target.style.display = 'none'; }} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                    )
                  ) : (
                    <div style={{ width: '48px', height: '48px', background: '#2a2a2a', borderRadius: '6px', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', color: post.is_profile_post ? '#8b5cf6' : '#ff4500', fontWeight: '600' }}>
                      {post.is_profile_post ? '📌 Profile Post' : (post.subreddits?.name || getSubredditName(post.subreddit_id))}
                      {post.needs_scraping && (
                        <span style={{ fontSize: '10px', color: '#8b5cf6', background: '#1a0a2a', padding: '1px 6px', borderRadius: '8px', marginLeft: '6px', border: '1px solid #7c3aed' }}>⏳ Pending scrape</span>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: post.title ? '#ffffff' : '#555', marginTop: '2px' }}>
                      {post.title || 'No title'}
                    </div>
                    {editingPostUrl === post.id ? (
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                        <input
                          value={editingPostUrlValue}
                          onChange={e => setEditingPostUrlValue(e.target.value)}
                          placeholder="https://www.reddit.com/r/..."
                          autoFocus
                          style={{ flex: 1, fontSize: '11px', padding: '4px 8px', background: '#0f0f0f', border: '1px solid #333', borderRadius: '6px', color: '#fff' }}
                        />
                        <button onClick={() => savePostUrl(session.id, post.id, editingPostUrlValue)} style={{ fontSize: '11px', padding: '4px 8px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
                        <button onClick={() => setEditingPostUrl(null)} style={{ fontSize: '11px', padding: '4px 8px', background: 'transparent', color: '#666', border: '1px solid #333', borderRadius: '6px', cursor: 'pointer' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                        {post.post_url ? (
                          <a href={post.post_url} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: '#ff4500', textDecoration: 'none' }}>🔗 Open post</a>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#555' }}>No link</span>
                        )}
                        <button onClick={() => { setEditingPostUrl(post.id); setEditingPostUrlValue(post.post_url || ''); }}
                          style={{ fontSize: '10px', padding: '2px 6px', background: 'transparent', color: '#666', border: '1px solid #333', borderRadius: '4px', cursor: 'pointer' }}
                          title="Edit post URL">✏️</button>
                        {post.post_url && !post.needs_scraping && (
                          <button onClick={() => flagPostRescrape(session.id, post.id)}
                            style={{ fontSize: '10px', padding: '2px 6px', background: 'transparent', color: '#666', border: '1px solid #333', borderRadius: '4px', cursor: 'pointer' }}
                            title="Schedule for re-scraping">🔄</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {expandedSession === session.id && !isCompleted && (
        <div className="expanded-list">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '13px', color: '#aaaaaa' }}>{'\uD83D\uDCC5'} Change date:</span>
            <input
              type="date"
              min={getMinDate()}
              max={getMaxDate()}
              defaultValue={session.scheduled_date}
              style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #333', background: '#1a1a1a', color: '#fff' }}
              onChange={e => { if (e.target.value) updateSessionDate(session.id, e.target.value); }}
            />
          </div>

          

          {session.status === 'ready' && (
            <div style={{ padding: '8px 12px', background: '#1a1a00', border: '1px solid #92400e', borderRadius: '8px', marginBottom: '12px', fontSize: '12px', color: '#f59e0b' }}>
              🔒 Session is locked while Ready. Move back to Draft to make changes.
            </div>
          )}
          <div className="add-posts-row" style={{ opacity: session.status === 'ready' ? 0.4 : 1, pointerEvents: session.status === 'ready' ? 'none' : 'auto' }}>
            <select onChange={e => { if (e.target.value) { addPostFromList(session.id, e.target.value); e.target.value = ''; } }}>
  <option value="">+ Add all subreddits from a posting list</option>
  {postingLists.filter(list => {
  const creatorAccIds = accounts.filter(a => a.creator_id === selectedCreator?.id).map(a => a.id);
  return creatorAccIds.length === 0 || creatorAccIds.includes(list.account_id);
}).map(list => (
    <option key={list.id} value={list.id}>{list.name}</option>
  ))}
</select>
            <button
              className="btn-secondary"
              onClick={async () => {
                setSubPickerModal({ sessionId: session.id }); setSubPickerSearch(''); setSubPickerTags([]); setSubPickerSelected([]);
                if (subreddits.length === 0) {
                  try {
                    const res = await axios.get(`${API}/api/subreddits/names`);
                    setSubreddits(res.data);
                  } catch (err) { notifyError(err, 'Could not load subreddits'); }
                }
              }}
            >
              + Add individual subreddit
            </button>
            <button
              className="btn-secondary"
              onClick={() => { setBulkAddModal({ sessionId: session.id }); setBulkAddText(''); }}
            >
              📋 Bulk Add
            </button>
            <button
              className="btn-secondary"
              onClick={() => openApprovedModal(session)}
              title="Pick from this account's approved subs in the Planner"
            >
              ✅ Add from approved list
            </button>
          </div>

          {!(session.scheduled_posts || []).some(p => p.is_profile_post) && session.status !== 'ready' && (
            <div style={{
              padding: '8px 12px', background: '#1a0a2a', border: '1px solid #7c3aed',
              borderRadius: '8px', marginBottom: '12px', marginTop: '8px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ fontSize: '12px', color: '#a78bfa' }}>
                📌 No profile post — your followers won't see this session in their feed
              </span>
              <button
                className="btn-secondary"
                style={{ fontSize: '11px', padding: '4px 12px', borderColor: '#7c3aed', color: '#a78bfa' }}
                onClick={() => addProfilePost(session.id)}
              >+ Add Profile Post</button>
            </div>
          )}

          {(session.scheduled_posts || []).length === 0 ? (
            <p className="empty">No posts added yet. Add subreddits above.</p>
          ) : (
            <div className="posts-list">
              {(session.scheduled_posts || []).slice().sort((a, b) => { if (a.is_profile_post && !b.is_profile_post) return -1; if (!a.is_profile_post && b.is_profile_post) return 1; return new Date(a.created_at) - new Date(b.created_at); }).map(post => {
                const subName = post.subreddits?.name || getSubredditName(post.subreddit_id);
                const subUrl = getSubredditUrl(subName);
                const hasTitleReq = !!post.subreddits?.title_requirements;
                const isEditingThisTitleReq = editingTitleReq === post.subreddit_id;
                const isIncomplete = !post.is_profile_post && (!post.title || !post.content_url);
                const otherAccountWhy = !post.is_profile_post && closedWhy(session.account_id, post.subreddit_id);

                return (
                  <div className="post-card" key={post.id} style={{
                    borderColor: post.is_profile_post ? '#8b5cf6' : otherAccountWhy ? '#ef4444' : (intervalStatus[post.subreddit_id]?.blocked ? '#ff4500' : isIncomplete ? '#f59e0b' : '#2a2a2a'),
                    borderLeftWidth: (post.is_profile_post || isIncomplete) ? '3px' : undefined,
                    borderLeftColor: post.is_profile_post ? '#8b5cf6' : isIncomplete ? '#f59e0b' : undefined
                  }}>
                   <div className="post-card-header">
                      {post.is_profile_post ? (
                        <>
                          <span style={{ color: '#8b5cf6', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            📌 Profile Post · u/{accounts.find(a => a.id === session.account_id)?.reddit_username || 'unknown'}
                          </span>
                          {session.status !== 'ready' && (
                            <button className="btn-danger btn-small" style={{ marginLeft: 'auto' }}
                              onClick={() => {
                                if (window.confirm('Remove profile post? Your followers won\'t see this session in their feed.')) {
                                  deletePost(session.id, post.id);
                                }
                              }}>Remove</button>
                          )}
                        </>
                      ) : (
                        <>
                          <a
                            href={subUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#ffffff', fontWeight: '700', textDecoration: 'none', borderBottom: '1px solid #555' }}
                            onMouseEnter={e => e.target.style.color = '#ff4500'}
                            onMouseLeave={e => e.target.style.color = '#ffffff'}
                          >
                            {subName}
                          </a>
                          {intervalStatus[post.subreddit_id]?.blocked && (
                            <span style={{ fontSize: '11px', color: '#ff4500', marginLeft: '8px' }}>
                              {'\u23F1'} {intervalStatus[post.subreddit_id].hoursRemaining}h remaining
                            </span>
                          )}
                          {otherAccountWhy && (
                            <span title={`Another account of this model uses this sub (${otherAccountWhy}). This account stays out of it: remove this post \u2014 auto-fill leaves it empty.`} style={{
                              fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px',
                              background: '#2a0a0a', color: '#ef4444', border: '1px solid #7f1d1d', marginLeft: '6px'
                            }}>
                              \uD83D\uDC65 another account's sub \u2014 remove
                            </span>
                          )}
                          {isIncomplete && (
                            <span style={{
                              fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px',
                              background: '#2a1a00', color: '#f59e0b', border: '1px solid #92400e', marginLeft: '6px'
                            }}>
                              ⚠ {!post.title && !post.content_url ? 'no title & no content' : !post.title ? 'no title' : 'no content'}
                            </span>
                          )}
                          <button
                            className="btn-danger btn-small"
                            style={{ marginLeft: '6px' }}
                            onClick={() => {
                              setRulesModal(post.subreddits || { id: post.subreddit_id, name: subName, rules: '' });
                              setRulesValue(post.subreddits?.rules || '');
                              setEditingRules(false);
                            }}
                          >
                            Rules
                          </button>
                          {session.status !== 'ready' && (
                            <button className="btn-danger btn-small" style={{ marginLeft: 'auto' }} onClick={() => deletePost(session.id, post.id)}>Remove from posting</button>
                          )}
                        </>
                      )}
                    </div>

                    {!post.is_profile_post && (
                      <>
                        {isEditingThisTitleReq ? (
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                            <input
                              value={titleReqValue}
                              onChange={e => setTitleReqValue(e.target.value)}
                              placeholder="Title requirements"
                              style={{ flex: 1, fontSize: '12px' }}
                            />
                            <button onClick={() => saveTitleRequirements(post.subreddit_id)}>Save</button>
                            <button className="btn-secondary" onClick={() => setEditingTitleReq(null)}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                            <span
                              onClick={() => { setEditingTitleReq(post.subreddit_id); setTitleReqValue(post.subreddits?.title_requirements || ''); }}
                              style={{
                                fontSize: '11px', cursor: 'pointer',
                                color: hasTitleReq ? '#f59e0b' : '#ef4444',
                                background: hasTitleReq ? '#2a1a00' : '#2a0a0a',
                                border: `1px solid ${hasTitleReq ? '#92400e' : '#7f1d1d'}`,
                                borderRadius: '6px', padding: '2px 8px'
                              }}
                            >
                              {hasTitleReq ? `📝 ${post.subreddits.title_requirements}` : '⚠️ No title requirements'}
                            </span>
                            {post.subreddits?.moderation_warning && (
                              <span style={{
                                fontSize: '11px', color: '#3b82f6',
                                background: '#0f1f3d', border: '1px solid #1d4ed8',
                                borderRadius: '6px', padding: '2px 8px'
                              }}>🗣️ {post.subreddits.moderation_warning}</span>
                            )}
                            {post.subreddits?.bot_warning && (
                              <span style={{
                                fontSize: '11px', color: '#ef4444',
                                background: '#2a0a0a', border: '1px solid #7f1d1d',
                                borderRadius: '6px', padding: '2px 8px'
                              }}>🤖 {post.subreddits.bot_warning}</span>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <PostTitleInput
                          key={post._titleKey || post.id}
                          postId={post.id}
                          sessionId={session.id}
                          initialValue={post.title}
                          disabled={session.status === 'ready'}
                          onSave={handleTitleSave}
                        />
                        {(() => {
                          const v = titleRuleViolations(post.title, post.subreddits);
                          const flairs = Array.isArray(post.subreddits?.flairs) ? post.subreddits.flairs : [];
                          const chosenFlair = post.flair_id || post.flair_auto?.flair_id || '';
                          // flairs = [] means the post form offered none (Reddit's
                          // "required" flag with flair switched off for posters)
                          const noFlairOffered = Array.isArray(post.subreddits?.flairs) && post.subreddits.flairs.length === 0;
                          const flairGap = post.subreddits?.flair_required && !chosenFlair && !post.subreddits?.default_flair_id && !noFlairOffered;
                          const fit = postFitIssue(post);
                          const pickWhy = post.pick_reason || null;
                          const creatorWarn = !post.is_profile_post && post.sub_caps?.not_for_creators
                            ? (post.sub_caps.not_for_creators_reason || 'the sub is for something other than creators posting their own content') : null;
                          const showFlair = !post.is_profile_post && flairs.length > 0;
                          if (!v.length && !flairGap && !fit && !showFlair && !creatorWarn && !pickWhy) return null;
                          const why = post.flair_source === 'manual' ? 'picked by you'
                            : post.flair_id ? `auto: ${post.flair_why || post.flair_source || ''}`
                            : post.flair_auto ? `auto: ${post.flair_auto.flair_why}` : null;
                          return (
                            <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {showFlair && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '11px', color: post.subreddits?.flair_required ? '#f59e0b' : '#888' }}>
                                    🏷 Flair{post.subreddits?.flair_required ? ' (required)' : ''}:
                                  </span>
                                  <select
                                    value={chosenFlair}
                                    disabled={session.status === 'ready'}
                                    onChange={e => handleFlairChange(session.id, post, e.target.value)}
                                    style={{ fontSize: '11px', padding: '2px 6px', background: '#141414', color: '#ddd', border: `1px solid ${!chosenFlair && post.subreddits?.flair_required ? '#b45309' : '#333'}`, borderRadius: '6px', maxWidth: '260px' }}
                                  >
                                    <option value="">{post.subreddits?.flair_required ? '— pick a flair —' : '— no flair —'}</option>
                                    {flairs.map(f => (
                                      <option key={f.id} value={f.id}>{(f.label || '').replace(/:[a-z0-9_+-]+:/gi, '').trim() || f.label}</option>
                                    ))}
                                  </select>
                                  {why && chosenFlair && <span style={{ fontSize: '10px', color: '#666' }}>{why}</span>}
                                  {post.flair_source === 'manual' && (
                                    <button onClick={() => handleFlairChange(session.id, post, '')} title="Let the tool pick the flair from the content again"
                                      style={{ fontSize: '10px', background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0 }}>↺ auto</button>
                                  )}
                                </div>
                              )}
                              {pickWhy && post.content_url && (
                                <div style={{ fontSize: '10px', color: '#666' }} title="Why the auto-picker chose this item for this sub">
                                  🎯 picked: {pickWhy}
                                </div>
                              )}
                              {creatorWarn && (
                                <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 600 }}
                                  title="From the sub's own rules. Allowed to post, but as a regular user: no promotion, nothing that reads like a creator.">
                                  ⚠ Not for creator posts: {creatorWarn}
                                </div>
                              )}
                              {fit && (
                                <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: 600 }}
                                  title="From Reddit's own settings for this sub, or a removal we learned from. The extension will stop before uploading this.">
                                  ⛔ Can't post this here: {fit}
                                </div>
                              )}
                              {v.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#ef4444' }}
                                  title="Reddit auto-removes titles that break these rules">
                                  ⛔ Title: {v.join(' · ')}
                                </div>
                              )}
                              {flairGap && (flairs.length ? (
                                <div style={{ fontSize: '11px', color: '#f59e0b' }}
                                  title="Or set a default post flair for this sub on the Subreddits page">
                                  🏷 Flair required — pick one above, or auto-posting will stop here
                                </div>
                              ) : (
                                <div style={{ fontSize: '11px', color: '#888' }}
                                  title="Reddit's settings for this sub say flair is required, but we don't have its flair list. Some subs keep that setting with flair switched off, so the extension goes by the post form.">
                                  🏷 Reddit marks flair as required, flair list not known — the extension checks the post form: no flair there → posts without; several to choose from → stops for a pick
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      {post.content_url && session.status !== 'ready' && (
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          {aiPresets.length > 0 && (
                            <select
                              value={selectedPresetId}
                              onChange={e => selectPreset(e.target.value)}
                              style={{
                                fontSize: '10px', padding: '4px 6px', background: '#1a1a1a',
                                color: '#f59e0b', border: '1px solid #444', borderRadius: '6px',
                                height: '34px', maxWidth: '100px'
                              }}
                              title="Select AI preset"
                            >
                              <option value="">Default</option>
                              {aiPresets.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          )}
                          <button
                            id={`ai-btn-${post.id}`}
                            disabled={aiGenerating}
                          onClick={async (e) => {
                            if (aiGenerating) return;
                            setAiGenerating(true);
                            const btn = e.currentTarget;
                            btn.disabled = true;
                            const origText = btn.textContent;
                            btn.textContent = '⏳ Generating...';
                            showActivity('Generating AI title...');
                            try {
                              const preset = aiPresets.find(p => p.id === selectedPresetId);
                              const account = accounts.find(a => a.id === session.account_id);
                              // Build carousel info if applicable
                              const isCarousel = post.carousel_items && post.carousel_items.length > 1;
                              const carouselImages = isCarousel ? post.carousel_items.map((ci, idx) => ({
                                image_url: ci.thumbnail_url || ci.content_url,
                                vault_item_id: ci.vault_item_id,
                                position: idx + 1
                              })) : null;

                              const res = await axios.post(
                                `${API}/api/ai/generate-title`,
                                {
                                  image_url: post.thumbnail_url || post.content_url,
                                  vault_item_id: post.vault_item_id || null,
                                  subreddit: post.subreddits?.name || '',
                                  subreddit_id: post.subreddit_id || null,
                                  title_requirements: post.subreddits?.title_requirements || '',
                                  rules: post.subreddits?.rules || '',
                                  general_info: post.subreddits?.general_info || '',
                                  character_name: preset?.name_input || '',
                                  creator_name: selectedCreator?.name || '',
                                  reddit_username: account?.reddit_username || '',
                                  account_id: account?.id || session.account_id || null,
                                  custom_prompt: preset?.llm_prompt || '',
                                  temperature: preset?.temperature,
                                  is_carousel: isCarousel,
                                  carousel_images: carouselImages
                                },
                                { timeout: 120000 }
                              );
                              if (res.data.titles && res.data.titles.length > 0) {
                                setTitlePicker({ sessionId: session.id, postId: post.id, titles: res.data.titles });
                              }
                            } catch (err) {
                              notifyError(err, 'Title generation failed');
                            }
                            hideActivity();
                            btn.disabled = false;
                            btn.textContent = origText;
                            setAiGenerating(false);
                          }}
                            title="Generate AI title based on image and subreddit"
                            style={{
                              background: '#2a1a00', border: '1px solid #f59e0b', color: '#f59e0b',
                              borderRadius: '6px', padding: '4px 12px', cursor: 'pointer',
                              fontSize: '11px', fontWeight: '600', height: '34px',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f59e0b'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#2a1a00'; e.currentTarget.style.color = '#f59e0b'; }}
                          >✨ Generate title</button>
                        </div>
                      )}
                      {session.status !== 'ready' && (
                        <select
                          value={post.spoof_preset || session.spoof_preset || 'medium'}
                          onChange={e => updatePost(session.id, post.id, { spoof_preset: e.target.value })}
                          title="Spoof level for this post"
                          style={{
                            fontSize: '11px', padding: '4px 8px', background: '#2a1a00',
                            color: '#f59e0b', border: '1px solid #f59e0b', borderRadius: '6px',
                            height: '34px', flexShrink: 0, cursor: 'pointer', fontWeight: '600',
                            appearance: 'none', WebkitAppearance: 'none',
                            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'5\'%3E%3Cpath d=\'M0 0l4 5 4-5z\' fill=\'%23f59e0b\'/%3E%3C/svg%3E")',
                            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center',
                            paddingRight: '20px',
                            maxWidth: '110px', width: 'auto'
                          }}
                        >
                          <option value="medium">🔄 Medium</option>
                          <option value="high">🔄 Hard</option>
                        </select>
                      )}
                    </div>

                    <div className="content-type-row">
                      <select
                        defaultValue={post.content_type || 'file'}
                        onChange={e => updatePost(session.id, post.id, { content_type: e.target.value })}
                      >
                        <option value="file">Upload Image / GIF</option>
                        <option value="link">Link / URL</option>
                      </select>
                      {post.content_type !== 'link' ? (
                        <div className="file-upload-area">
                          {post.content_url && (
                    <div style={{ marginBottom: '8px' }}>
                      {(post.carousel_items && post.carousel_items.length > 1) ? (
                        <div>
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', overflowX: 'auto', paddingBottom: '4px' }}>
                            {post.carousel_items.map((ci, i) => {
                              const canMove = session.status !== 'ready';
                              const last = post.carousel_items.length - 1;
                              const dropHere = carouselDropAt && carouselDropAt.postId === post.id && carouselDropAt.index === i;
                              const arrow = { position: 'absolute', bottom: '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(0,0,0,0.75)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '10px', lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
                              return (
                              <div key={ci.vault_item_id || ci.content_url || i} style={{ position: 'relative', flexShrink: 0, opacity: carouselDragRef.current && carouselDragRef.current.postId === post.id && carouselDragRef.current.from === i ? 0.4 : 1 }}
                                draggable={canMove}
                                onDragStart={canMove ? (e) => { carouselDragRef.current = { postId: post.id, from: i }; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (err) {} } : undefined}
                                onDragOver={canMove ? (e) => {
                                  const d = carouselDragRef.current;
                                  if (!d || d.postId !== post.id) return;
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                  if (!dropHere) setCarouselDropAt({ postId: post.id, index: i });
                                } : undefined}
                                onDrop={canMove ? (e) => {
                                  const d = carouselDragRef.current;
                                  if (!d || d.postId !== post.id) return;
                                  e.preventDefault();
                                  carouselDragRef.current = null;
                                  setCarouselDropAt(null);
                                  moveCarouselItem(session.id, post, d.from, i);
                                } : undefined}
                                onDragEnd={canMove ? () => { carouselDragRef.current = null; setCarouselDropAt(null); } : undefined}>
                                <img src={ci.thumbnail_url || ci.content_url} onError={(e) => { if (ci.content_url && e.target.src !== ci.content_url) e.target.src = ci.content_url; else e.target.style.display = 'none'; }} alt=""
                                  draggable={false}
                                  onClick={() => setLightbox({ sessionId: session.id, postId: post.id, index: i })}
                                  title={canMove ? 'Click to enlarge · drag to change the order (1 = the cover)' : 'Click to enlarge'}
                                  style={{ height: '80px', width: '80px', objectFit: 'cover', borderRadius: '6px', cursor: canMove ? 'grab' : 'zoom-in', display: 'block',
                                    border: dropHere ? '2px dashed #f59e0b' : i === 0 ? '2px solid #2563eb' : '1px solid #2a2a2a' }} />
                                <span style={{
                                  position: 'absolute', top: '3px', left: '3px',
                                  width: '16px', height: '16px', borderRadius: '50%',
                                  background: i === 0 ? '#2563eb' : 'rgba(0,0,0,0.7)',
                                  color: '#fff', fontSize: '9px', fontWeight: '700',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>{i + 1}</span>
                                {canMove && (
                                  <button onClick={() => deleteCarouselItem(session.id, post, i)}
                                    title="Remove this image from the carousel"
                                    style={{ position: 'absolute', top: '3px', right: '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(0,0,0,0.75)', color: '#f87171', border: 'none', cursor: 'pointer', fontSize: '12px', lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>
                                )}
                                {canMove && i > 0 && (
                                  <button onClick={() => moveCarouselItem(session.id, post, i, i - 1)} title={i === 1 ? 'Move left (makes it the cover)' : 'Move left'}
                                    style={{ ...arrow, left: '3px' }}>◀</button>
                                )}
                                {canMove && i < last && (
                                  <button onClick={() => moveCarouselItem(session.id, post, i, i + 1)} title="Move right"
                                    style={{ ...arrow, right: '3px' }}>▶</button>
                                )}
                              </div>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span title={session.status !== 'ready' ? 'Posted in this order, picture 1 is the cover. Drag the pictures or use ◀ ▶ to change it.' : 'Posted in this order, picture 1 is the cover'}
                              style={{ fontSize: '11px', color: '#2563eb', fontWeight: '600', background: '#0a0a1a', padding: '2px 8px', borderRadius: '6px', border: '1px solid #1d4ed8' }}>
                              📸 Carousel · {post.carousel_items.length} images
                            </span>
                            {session.status !== 'ready' && (
                              <button onClick={() => removeContent(session.id, post.id)} className="btn-secondary" style={{ fontSize: '11px', padding: '3px 10px' }}>Remove all</button>
                            )}
                            {session.status !== 'ready' && post.subreddit_id && (
                              <button onClick={() => repickPost(session.id, post.id)} disabled={repicking === post.id}
                                title="Build a different carousel from another matching folder"
                                className="btn-secondary" style={{ fontSize: '11px', padding: '3px 10px' }}>
                                {repicking === post.id ? '…' : '🎲 Re-roll'}
                              </button>
                            )}
                            <button onClick={() => setLightbox({ sessionId: session.id, postId: post.id, index: 0 })}
                              className="btn-secondary" style={{ fontSize: '11px', padding: '3px 10px' }}>🔍 Enlarge</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {/* key: remount on content change so a previous
                              display:none (set imperatively by onError) can't
                              stick and hide a newly repicked image.
                              onLoad: also un-hide if the node is reused. */}
                          <img key={post.vault_item_id || post.content_url} src={post.thumbnail_url || post.content_url} alt="preview"
                            onLoad={(e) => { e.target.style.display = 'block'; }}
                            onError={(e) => { if (post.content_url && e.target.src !== post.content_url) e.target.src = post.content_url; else e.target.style.display = 'none'; }}
                            onClick={() => setLightbox({ sessionId: session.id, postId: post.id, index: 0 })} title="Click to enlarge"
                            style={{ maxHeight: '120px', objectFit: 'contain', borderRadius: '6px', display: 'block', marginBottom: '6px', cursor: 'zoom-in' }} />
                          {session.status !== 'ready' && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => removeContent(session.id, post.id)}
                                className="btn-secondary" style={{ fontSize: '11px', padding: '3px 10px' }}>Remove image</button>
                              {isKarmaIdeaContent(post) ? (
                                <KarmaGenerateButton session={session} post={post} regenerate
                                  onDone={saved => fillFromKarmaIdea(session.id, post.id, saved)} />
                              ) : post.subreddit_id && (
                                <button onClick={() => repickPost(session.id, post.id)} disabled={repicking === post.id}
                                  title="Swap in a different matched item for this slot"
                                  className="btn-secondary" style={{ fontSize: '11px', padding: '3px 10px' }}>
                                  {repicking === post.id ? '…' : '🎲 Repick'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {post.content_url || !karmaPosting.known(session.account_id) ? null : session.status === 'ready' ? (
                              <span style={{ fontSize: '12px', color: '#555' }}>🔒 Locked while ready</span>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*,video/gif"
                                  onChange={e => { if (e.target.files[0]) uploadFile(session.id, post.id, e.target.files[0]); }}
                                />
                                {karmaPosting.has(session.account_id) && !post.is_profile_post && (
                                  <KarmaGenerateButton session={session} post={post}
                                    onDone={saved => fillFromKarmaIdea(session.id, post.id, saved)} />
                                )}
                                {(() => {
                                  return (
                                    <button
                                      style={{
                                        whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px',
                                        background: '#ff4500', color: '#ffffff', border: 'none',
                                        borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
                                      }}
                                      onClick={() => openVaultPicker(session, post)}
                                    >
                                      📁 Vault
                                    </button>
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <input
                          placeholder="Paste link here (imgur, etc.)"
                          defaultValue={post.content_url}
                          onBlur={e => { if (e.target.value !== (post.content_url || '')) updatePost(session.id, post.id, { content_url: e.target.value }); }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Scheduler</h1>
          <p>Prepare and schedule posts up to 30 days in advance.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowPlanner(!showPlanner)} className="btn-secondary"
            style={{ fontSize: '13px', padding: '6px 16px', borderColor: '#8b5cf6', color: '#8b5cf6' }}>
            {showPlanner ? 'Hide Planner' : '📋 Planner'}
          </button>
          <button onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Schedule Posts'}
          </button>
        </div>
      </div>

      {showAccountTabs && <AccountTabs accounts={modelAccounts} sessions={sessions} activeId={tabAccount?.id || 'all'} onPick={pickAccountTab} />}

      {showPlanner && accounts.length > 0 && (
        <div className="card" style={{ borderColor: '#8b5cf6' }}>
          <Planner
            creatorId={selectedCreator?.id}
            accounts={shownAccounts}
            onSessionsChanged={() => { fetchSessions(); setCommentPlansKey(k => k + 1); }}
          />
        </div>
      )}

      <CommentPlans key={commentPlansKey} accounts={shownAccounts} />

      {showForm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowForm(false)}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '28px',
            width: '90%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px 0' }}>Schedule Posts</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
              <select
                value={form.account_id}
                onChange={e => {
                  const account = accounts.find(a => a.id === e.target.value);
                  const now = new Date();
                  const today = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
                  const autoName = account
                    ? `${today} · ${selectedCreator?.name || ''} · u/${account.reddit_username}`
                    : '';
                  setForm({ ...form, account_id: e.target.value, name: autoName });
                }}
              >
                <option value="">Select account</option>
                {modelAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} (u/{account.reddit_username})
                  </option>
                ))}
              </select>
              <input
                placeholder="Session name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>Posting date (today up to 30 days ahead)</label>
                <input
                  type="date"
                  min={getMinDate()}
                  max={getMaxDate()}
                  value={form.scheduled_date}
                  onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ background: '#0f0f0f', borderRadius: '10px', padding: '16px', border: '1px solid #2a2a2a', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#8b5cf6', fontWeight: '700', marginBottom: '14px' }}>📌 Profile Post</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#ffffff' }}>Include profile post</div>
                  <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>Posts to your profile feed so followers see it</div>
                </div>
                <div
                  onClick={() => setForm(prev => ({ ...prev, include_profile_post: !prev.include_profile_post }))}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px', cursor: 'pointer',
                    background: form.include_profile_post ? '#8b5cf6' : '#333',
                    transition: 'background 0.2s', position: 'relative', flexShrink: 0
                  }}
                >
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: '2px',
                    left: form.include_profile_post ? '22px' : '2px',
                    transition: 'left 0.2s'
                  }} />
                </div>
              </div>
            </div>

            <div style={{ background: '#0f0f0f', borderRadius: '10px', padding: '16px', border: '1px solid #2a2a2a', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '700', marginBottom: '14px' }}>🔄 Spoof Settings</div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>🖼 Images</label>
                  <select
                    value={form.spoof_preset}
                    onChange={e => setForm({ ...form, spoof_preset: e.target.value })}
                    style={{ width: '100%', fontSize: '13px', padding: '8px', background: '#1a1a1a', color: '#fff', border: '1px solid #333', borderRadius: '8px' }}
                  >
                    <option value="medium">Medium</option>
                    <option value="high">Hard</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>🎞 GIFs</label>
                  <select
                    value={form.spoof_preset_gif}
                    onChange={e => setForm({ ...form, spoof_preset_gif: e.target.value })}
                    style={{ width: '100%', fontSize: '13px', padding: '8px', background: '#1a1a1a', color: '#fff', border: '1px solid #333', borderRadius: '8px' }}
                  >
                    <option value="medium">Medium</option>
                    <option value="high">Hard</option>
                  </select>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#555', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 6px 0' }}><b style={{ color: '#aaaaaa' }}>Medium:</b> Subtle changes — random JPEG quality (70-87), gentle brightness shifts. Best for routine posting.</p>
                <p style={{ margin: 0 }}><b style={{ color: '#aaaaaa' }}>Hard:</b> Aggressive changes — deterministic quality cycling every 2 variants, stronger brightness shifts. Best for high-volume reposting.</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button onClick={createSession} disabled={loading}>
                {loading ? 'Creating...' : 'Create Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Caption Settings */}
      <div className="card" style={{ borderColor: showAiSettings ? '#f59e0b' : '#2a2a2a' }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowAiSettings(!showAiSettings)}
        >
          <h2 style={{ margin: 0, fontSize: '14px' }}>✨ AI Title Generation — Presets</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {selectedPresetId && (
              <span style={{ fontSize: '11px', color: '#f59e0b' }}>
                Active: {aiPresets.find(p => p.id === selectedPresetId)?.name || 'Default'}
              </span>
            )}
            <span style={{ color: '#aaaaaa', fontSize: '13px' }}>{showAiSettings ? '▲' : '▼'}</span>
          </div>
        </div>
        {showAiSettings && (
          <div style={{ marginTop: '16px' }}>
            {/* Existing presets */}
            {aiPresets.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#aaaaaa', marginBottom: '8px' }}>SAVED PRESETS ({aiPresets.length}/5)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {aiPresets.map(preset => (
                    <div key={preset.id} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      background: selectedPresetId === preset.id ? '#2a1a00' : '#0f0f0f',
                      border: selectedPresetId === preset.id ? '1px solid #f59e0b' : '1px solid #2a2a2a',
                      borderRadius: '8px', padding: '10px 12px'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: selectedPresetId === preset.id ? '#f59e0b' : '#fff' }}>
                          {preset.name}
                        </div>
                        <div style={{ fontSize: '10px', color: '#555', marginTop: '2px' }}>
                          T:{preset.temperature}{preset.name_input ? ` · 👤 ${preset.name_input}` : ''}{preset.llm_prompt ? ' · Custom prompt' : ' · Default prompt'}
                        </div>
                      </div>
                      <button
                        onClick={() => selectPreset(selectedPresetId === preset.id ? '' : preset.id)}
                        style={{
                          fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                          background: selectedPresetId === preset.id ? '#f59e0b' : '#2a2a2a',
                          color: selectedPresetId === preset.id ? '#fff' : '#aaaaaa'
                        }}
                      >{selectedPresetId === preset.id ? '✓ Active' : 'Use'}</button>
                      <button
                        onClick={() => {
                          setEditingPreset(preset);
                          setPresetForm({
                            name: preset.name,
                            name_input: preset.name_input || '',
                            llm_prompt: preset.llm_prompt || '',
                            temperature: preset.temperature || 0.95
                          });
                          setShowPresetForm(true);
                        }}
                        className="btn-secondary"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >Edit</button>
                      <button
                        onClick={() => deletePreset(preset.id)}
                        className="btn-danger"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add/Edit preset form */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '0' }}>
              {!showPresetForm && !editingPreset && aiPresets.length < 5 && (
                <button className="btn-secondary" style={{ fontSize: '12px' }} onClick={() => setShowPresetForm(true)}>
                  + New Preset
                </button>
              )}
              <button className="btn-secondary" style={{ fontSize: '12px' }} onClick={() => setShowAiHelp(!showAiHelp)}>
                {showAiHelp ? 'Hide Help' : '❓ Help'}
              </button>
            </div>

          

            {/* Help panel */}
            {showAiHelp && (
              <div style={{ background: '#0a0a1a', borderRadius: '8px', padding: '14px', border: '1px solid #1d4ed8', marginTop: '12px' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600', marginBottom: '10px' }}>How AI Title Generation Works (Grok 4.3)</div>
                <div style={{ fontSize: '11px', color: '#aaaaaa', lineHeight: '1.8' }}>
                  <p style={{ marginBottom: '8px' }}>Grok analyzes your image and generates 5 Reddit post title options. You pick the one you like.</p>
                  <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600', marginBottom: '4px' }}>Two-Step Process</div>
                  <p style={{ marginBottom: '8px' }}>
                    <b>Step 1 — Image Description</b>: Grok vision analyzes the image and creates a description. This is cached — only runs once per image.<br/>
                    <b>Step 2 — Title Generation</b>: Grok uses the description + subreddit context + your preset prompt to generate 5 title options.
                  </p>
                  <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600', marginBottom: '4px' }}>Available Placeholders</div>
                  <div style={{ background: '#111', borderRadius: '6px', padding: '8px 10px', fontSize: '10px', fontFamily: 'monospace', lineHeight: '2' }}>
                    <div><span style={{ color: '#f59e0b' }}>{'{character_name}'}</span> — Character/persona name from preset</div>
                    <div><span style={{ color: '#f59e0b' }}>{'{subreddit}'}</span> — Target subreddit (e.g. r/amihot)</div>
                    <div><span style={{ color: '#f59e0b' }}>{'{title_requirements}'}</span> — Subreddit's title requirements</div>
                    <div><span style={{ color: '#f59e0b' }}>{'{general_info}'}</span> — Subreddit description</div>
                    <div><span style={{ color: '#f59e0b' }}>{'{rules}'}</span> — Subreddit rules</div>
                    <div><span style={{ color: '#f59e0b' }}>{'{creator_name}'}</span> — Selected creator name</div>
                    <div><span style={{ color: '#f59e0b' }}>{'{reddit_username}'}</span> — Reddit username</div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '600', marginTop: '10px', marginBottom: '4px' }}>Example</div>
                  <p style={{ marginBottom: '4px' }}>Character name: <span style={{ color: '#fff', fontFamily: 'monospace' }}>Dark Elf</span></p>
                  <p style={{ marginBottom: '4px' }}>Title requirements: <span style={{ color: '#fff', fontFamily: 'monospace' }}>Must include F22 {'{character_name}'}</span></p>
                  <p style={{ marginBottom: '4px' }}>Prompt: <span style={{ color: '#fff', fontFamily: 'monospace' }}>Write a flirty title for {'{subreddit}'}. {'{title_requirements}'}. One sentence, casual.</span></p>
                  <p>Result: <span style={{ color: '#22c55e' }}>F22 Dark Elf cosplay — want me to cast a spell on you?</span></p>
                </div>
              </div>
            )}

            {/* Preset form */}
            {(showPresetForm || editingPreset) && (
              <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '14px', border: '1px solid #2a2a2a', marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600', marginBottom: '12px' }}>
                  {editingPreset ? `Edit: ${editingPreset.name}` : 'Create New Preset'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    placeholder="Preset name (e.g. Flirty NSFW)"
                    value={presetForm.name}
                    onChange={e => setPresetForm(prev => ({ ...prev, name: e.target.value }))}
                    style={{ fontSize: '13px' }}
                  />
                  
                  <div>
                    <label style={{ fontSize: '10px', color: '#3b82f6', display: 'block', marginBottom: '4px' }}>✍️ TITLE PROMPT (leave empty for default Grok prompt)</label>
                    <textarea
                      value={presetForm.llm_prompt}
                      onChange={e => setPresetForm(prev => ({ ...prev, llm_prompt: e.target.value }))}
                      placeholder="You are {character_name}. Write a flirty message for {subreddit}. {title_requirements}. One sentence, casual style."
                      rows={4}
                      style={{ width: '100%', fontSize: '12px', borderColor: '#1d4ed8' }}
                    />
                    <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>
                      Placeholders: {'{subreddit}'} {'{title_requirements}'} {'{general_info}'} {'{rules}'} {'{character_name}'} {'{creator_name}'} {'{reddit_username}'}
                    </div>
                  </div>
                  <div style={{ maxWidth: '300px' }}>
                    <label style={{ fontSize: '10px', color: '#aaaaaa', display: 'block', marginBottom: '2px' }}>🎨 Creativity ({presetForm.temperature})</label>
                    <input type="range" min="0" max="2" step="0.05" value={presetForm.temperature}
                      onChange={e => setPresetForm(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                      style={{ width: '100%' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={async () => { await savePreset(); setShowPresetForm(false); }} disabled={!presetForm.name.trim() || (aiPresets.length >= 5 && !editingPreset)}>
                      {editingPreset ? 'Update Preset' : `Save Preset (${aiPresets.length}/5)`}
                    </button>
                    <button className="btn-secondary" onClick={() => {
                      setEditingPreset(null);
                      setShowPresetForm(false);
                      setPresetForm({ name: '', name_input: '', llm_prompt: '', temperature: 0.95 });
                    }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ fontSize: '10px', color: '#555', marginTop: '10px' }}>
              Powered by Grok 4.3. Default prompt auto-includes subreddit context. Use placeholders in title requirements like: "Must include F20 {'{character_name}'}"
            </div>
          </div>
        )}
      </div>

      <KarmaModeNotice accounts={shownAccounts} postingIds={karmaPosting} />

      {Object.keys(sessionsByDate).filter(date =>
        sessionsByDate[date].some(s => s.status !== 'posted')
      ).length === 0 && !showCompleted ? (
        <div className="card">
          <p className="empty">No active sessions. Create one above.</p>
        </div>
      ) : null}

      {Object.keys(sessionsByDate).sort().filter(date =>
        sessionsByDate[date].some(s => s.status !== 'posted')
      ).map(date => (
        <div key={date}>
          <div className="date-header">
            {new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
            })}
          </div>
          {sessionsByDate[date].filter(s => s.status !== 'posted').map(session => renderSession(session))}
        </div>
      ))}


      <div className="card" style={{ marginTop: '24px' }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => {
            const newVal = !showCompleted;
            setShowCompleted(newVal);
            if (newVal) fetchCompletedSessions();
          }}
        >
          <h2>Completed Posts</h2>
          <span style={{ color: '#aaaaaa', fontSize: '13px' }}>{showCompleted ? '▲ Hide' : '▼ Show'}</span>
        </div>
        {showCompleted && (
          <div style={{ marginTop: '16px' }}>
            {(() => {
              const allCompleted = filteredSessions
                .filter(s => s.status === 'posted')
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              const paginated = allCompleted;

              if (allCompleted.length === 0) return <p className="empty">No completed sessions yet.</p>;

              return (
                <>
                  {(() => {
                const grouped = {};
                paginated.forEach(session => {
                  const date = session.scheduled_date;
                  if (!grouped[date]) grouped[date] = [];
                  grouped[date].push(session);
                });
                return Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a)).map(date => (
                  <div key={date}>
                    <div className="date-header">{new Date(date + 'T00:00:00').toLocaleDateString('en-GB', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                    })}</div>
                    {grouped[date].map(session => renderSession(session, true))}
                  </div>
                ));
              })()}
                  
                </>
              );
            })()}
          </div>
        )}
      </div>
      {titlePicker && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1002,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setTitlePicker(null)}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '16px' }}>✨ Pick a Title</h2>
              <button onClick={() => setTitlePicker(null)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {titlePicker.titles.map((title, i) => (
                <div
                  key={i}
                  onClick={() => applyPickedTitle(titlePicker.sessionId, titlePicker.postId, title)}
                  style={{
                    background: '#0f0f0f', border: '1px solid #333', borderRadius: '8px',
                    padding: '12px 14px', cursor: 'pointer', fontSize: '13px',
                    color: '#ffffff', lineHeight: '1.5', transition: 'border-color 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.background = '#1a1400'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.background = '#0f0f0f'; }}
                >
                  {title}
                </div>
              ))}
            </div>
            <button
              onClick={async () => {
                setTitlePicker(null);
                const btn = document.getElementById(`ai-btn-${titlePicker.postId}`);
                if (btn) btn.click();
              }}
              style={{
                width: '100%', marginTop: '12px', padding: '10px',
                background: '#2a2a2a', color: '#aaaaaa', border: '1px solid #444',
                borderRadius: '8px', cursor: 'pointer', fontSize: '12px'
              }}
            >🔄 Regenerate</button>
          </div>
        </div>
      )}
      {bulkAddModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '600px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>Bulk Add Subreddits</h2>
              <button onClick={() => setBulkAddModal(null)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ fontSize: '13px', color: '#aaaaaa', marginBottom: '12px', lineHeight: '1.6' }}>
              Paste subreddit names or URLs — one per line or comma-separated.<br />
              New subreddits are automatically created in the database.
            </div>
            <textarea
              value={bulkAddText}
              onChange={e => setBulkAddText(e.target.value)}
              placeholder={"r/funny\nr/pics\nhttps://www.reddit.com/r/aww\n\nor: funny, pics, aww"}
              autoFocus
              style={{
                flex: 1, minHeight: '200px', fontSize: '14px', padding: '14px',
                background: '#0f0f0f', color: '#fff', border: '1px solid #333',
                borderRadius: '8px', resize: 'vertical', fontFamily: 'monospace',
                lineHeight: '1.8'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <span style={{ fontSize: '13px', color: '#aaaaaa' }}>
                {bulkAddText.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0).length} subreddits detected
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-secondary" onClick={() => setBulkAddModal(null)}>Cancel</button>
                <button
                  onClick={() => bulkAddToSession(bulkAddModal.sessionId)}
                  disabled={bulkAddLoading || !bulkAddText.trim()}
                  style={{
                    background: '#22c55e', color: '#fff', border: 'none',
                    borderRadius: '8px', padding: '10px 24px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600',
                    opacity: bulkAddLoading || !bulkAddText.trim() ? 0.5 : 1
                  }}
                >
                  {bulkAddLoading ? '⏳ Adding...' : '+ Add All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {approvedModal && (() => {
        const session = sessions.find(x => x.id === approvedModal.sessionId);
        const inSession = new Set((session?.scheduled_posts || []).map(p => p.subreddit_id).filter(Boolean));
        // Why the weekly plan would skip a sub right now. Shown, never
        // enforced: the person picking decides.
        const holdOf = (s) => s.is_stopped ? 'stopped' : s.is_resting ? `resting ${s.rest_days_left}d`
          : s.is_dead ? 'dead sub' : s.no_images ? 'no images' : s.not_for_creators ? 'not for creators'
          : s.off_strategy ? 'off strategy' : null;
        const lastPost = (s) => {
          if (!s.last_posted_at) return 'never posted';
          const d = Math.floor((Date.now() - new Date(s.last_posted_at)) / 86400000);
          return d <= 0 ? 'posted today' : `last post ${d}d ago`;
        };
        const q = approvedSearch.trim().toLowerCase().replace(/^r\//, '');
        const list = (approvedModal.subs || []).filter(s => !q || (s.subreddit_name || '').toLowerCase().includes(q));
        const ready = list.filter(s => !inSession.has(s.subreddit_id) && !holdOf(s) && !s.closed);
        const toggle = (id) => setApprovedSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.8)', zIndex: 1001,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: '#1a1a1a', borderRadius: '12px', padding: '24px',
              width: '90%', maxWidth: '640px', maxHeight: '80vh',
              display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <h2 style={{ margin: 0 }}>Add from approved list</h2>
                <button onClick={() => setApprovedModal(null)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#777' }}>
                {session?.accounts?.reddit_username ? `u/${session.accounts.reddit_username} · ` : ''}subs approved in the Planner, biggest first
              </p>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  placeholder="Search approved subs..."
                  value={approvedSearch}
                  onChange={e => setApprovedSearch(e.target.value)}
                  autoFocus
                  style={{ flex: 1, fontSize: '14px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #333', background: '#0f0f0f', color: '#fff' }}
                />
                <button
                  className="btn-secondary"
                  disabled={approvedModal.loading || ready.length === 0}
                  onClick={() => setApprovedSelected(prev => [...new Set([...prev, ...ready.map(s => s.subreddit_id)])])}
                  title="Select every listed sub that isn't in this session and isn't resting, stopped, dead, off strategy or image-less"
                  style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
                >
                  Select ready ({ready.length})
                </button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1, borderRadius: '8px', border: '1px solid #2a2a2a' }}>
                {approvedModal.loading ? (
                  <p style={{ color: '#777', textAlign: 'center', padding: '24px' }}>⏳ Loading the approved list…</p>
                ) : list.length === 0 ? (
                  <p style={{ color: '#555', textAlign: 'center', padding: '24px' }}>
                    {(approvedModal.subs || []).length === 0 ? 'No approved subs for this account yet — approve some in the Planner.' : 'No approved subs match'}
                  </p>
                ) : list.map(s => {
                  const already = inSession.has(s.subreddit_id);
                  const locked = already || s.closed;
                  const hold = holdOf(s);
                  const isSelected = approvedSelected.includes(s.subreddit_id);
                  return (
                    <div
                      key={s.subreddit_id}
                      onClick={() => { if (!locked) toggle(s.subreddit_id); }}
                      style={{
                        padding: '10px 16px', cursor: locked ? 'default' : 'pointer', borderBottom: '1px solid #2a2a2a',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                        opacity: locked ? 0.45 : 1,
                        background: isSelected ? '#2a1000' : 'transparent',
                        borderLeft: isSelected ? '3px solid #ff4500' : '3px solid transparent'
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: isSelected ? '#ff4500' : '#ffffff' }}>
                          r/{String(s.subreddit_name || '').replace(/^r\//, '')}
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap', fontSize: '10px' }}>
                          <span style={{ color: '#777' }}>{lastPost(s)}</span>
                          {s.rhythm_label && <span style={{ color: '#777' }}>· {s.rhythm_label}</span>}
                          {already && <span style={{ background: '#2a2a2a', color: '#aaa', padding: '1px 8px', borderRadius: '10px' }}>in this session</span>}
                          {s.other_account && <span style={{ background: s.closed ? '#2a0a0a' : '#2a1f00', color: s.closed ? '#ef4444' : '#f59e0b', padding: '1px 8px', borderRadius: '10px' }}
                            title={s.closed ? 'Another account of this model uses it: this account stays out' : 'Flagged only'}>👥 {s.other_account.why}</span>}
                          {hold && <span style={{ background: '#2a1f00', color: '#f59e0b', padding: '1px 8px', borderRadius: '10px' }} title={typeof s.off_strategy === 'string' && hold === 'off strategy' ? s.off_strategy : undefined}>{hold}</span>}
                          {s.flair_gap && <span style={{ background: '#2a2a2a', color: '#aaa', padding: '1px 8px', borderRadius: '10px' }}>flair required</span>}
                        </div>
                      </div>
                      {s.reach > 0 && <span style={{ fontSize: '11px', color: '#555', whiteSpace: 'nowrap' }}>👀 {Number(s.reach).toLocaleString()}</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#555' }}>
                  {approvedSelected.length > 0 ? `${approvedSelected.length} selected` : `${list.length} approved`}
                  {approvedSelected.length > 0 && (
                    <button onClick={() => setApprovedSelected([])} style={{ marginLeft: '8px', background: 'transparent', border: 'none', color: '#777', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}>clear</button>
                  )}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn-secondary" onClick={() => setApprovedModal(null)}>Cancel</button>
                  <button disabled={approvedSelected.length === 0 || approvedAdding} onClick={addApprovedToSession}>
                    {approvedAdding ? '⏳ Adding…' : `Add${approvedSelected.length > 0 ? ` (${approvedSelected.length})` : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {subPickerModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 1001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '600px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>Add Subreddit</h2>
              <button onClick={() => setSubPickerModal(null)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <input
              placeholder="Search subreddits..."
              value={subPickerSearch}
              onChange={e => setSubPickerSearch(e.target.value)}
              autoFocus
              style={{ marginBottom: '12px', fontSize: '14px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #333', background: '#0f0f0f', color: '#fff' }}
            />
            {tags.length > 0 && (
              <TagFilter compact tags={subPickerTagNames} selected={subPickerTags} onChange={setSubPickerTags} />
            )}
            <div style={{ overflowY: 'auto', flex: 1, borderRadius: '8px', border: '1px solid #2a2a2a' }}>
              {filteredSubreddits.length === 0 ? (
                <p style={{ color: '#555', textAlign: 'center', padding: '24px' }}>No subreddits found</p>
              ) : (
                filteredSubreddits.map(sub => {
                  const isSelected = subPickerSelected.includes(sub.id);
                  return (
                    <div key={sub.id} onClick={() => setSubPickerSelected(prev => isSelected ? prev.filter(id => id !== sub.id) : [...prev, sub.id])} style={{
                      padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #2a2a2a',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: isSelected ? '#2a1000' : 'transparent',
                      borderLeft: isSelected ? '3px solid #ff4500' : '3px solid transparent'
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: isSelected ? '#ff4500' : '#ffffff' }}>{sub.name}</div>
                        {(sub.tags || []).length > 0 && (
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                            {(sub.tags || []).map(tag => (
                              <span key={tag} style={{ fontSize: '10px', background: '#2a2a2a', color: '#aaaaaa', padding: '2px 8px', borderRadius: '10px' }}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {sub.weekly_visitors && <span style={{ fontSize: '11px', color: '#555' }}>👀 {sub.weekly_visitors} weekly</span>}
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#555' }}>
                {subPickerSelected.length > 0 ? `${subPickerSelected.length} selected` : `${filteredSubreddits.length} subreddits`}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-secondary" onClick={() => setSubPickerModal(null)}>Cancel</button>
                <button disabled={subPickerSelected.length === 0} onClick={async () => {
                  for (const subId of subPickerSelected) { await addSinglePost(subPickerModal.sessionId, subId); }
                  setSubPickerModal(null);
                }}>
                  Add {subPickerSelected.length > 0 ? `(${subPickerSelected.length})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rulesModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '560px', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#aaaaaa', marginBottom: '4px' }}>Subreddit rules</div>
              <h2 style={{ margin: 0, color: '#ff4500' }}>{rulesModal.name}</h2>
            </div>
            {editingRules ? (
              <textarea value={rulesValue} onChange={e => setRulesValue(e.target.value)} placeholder="Enter subreddit rules..." style={{ width: '100%', minHeight: '160px', background: '#0f0f0f', border: '1px solid #444', borderRadius: '8px', color: '#ffffff', fontSize: '13px', padding: '10px', resize: 'vertical', marginBottom: '12px' }} />
            ) : (
              <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '14px', fontSize: '13px', color: rulesModal.rules ? '#ffffff' : '#555', minHeight: '80px', marginBottom: '16px', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {rulesModal.rules || 'No rules added yet.'}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {editingRules ? (
                <>
                  <button onClick={saveRules}>Save</button>
                  <button className="btn-secondary" onClick={() => { setEditingRules(false); setRulesValue(rulesModal.rules || ''); }}>Cancel</button>
                </>
              ) : (
                <>
                  <button className="btn-secondary" onClick={() => { setEditingRules(true); setRulesValue(rulesModal.rules || ''); }}>Edit</button>
                  <button className="btn-secondary" onClick={() => { setRulesModal(null); setEditingRules(false); }}>Close</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {autofillModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setAutofillModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: '12px', width: '92%', maxWidth: '460px', padding: '22px', fontFamily: 'inherit' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#fff', marginBottom: '4px' }}>✨ Auto-fill</div>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px', lineHeight: 1.5 }}>
              Fills every empty slot from the vault, never repeating an item in this session, one used in that sub recently, or one posted too often this month. Suggested values below come from your vault and this session's subs.
              {autofillPreview && <span style={{ color: '#aaa' }}> {autofillPreview.empty_slots} empty slots · vault: {autofillPreview.vault.images} images, {autofillPreview.vault.gifs} GIFs.</span>}
            </div>
            {autofillPreview?.main_first && (
              <div style={{ fontSize: '12px', color: '#fbbf24', background: '#221a0b', border: '1px solid #92400e', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', lineHeight: 1.5 }}>
                👥 Fill u/{autofillPreview.main_first.account} first: the main account still has {autofillPreview.main_first.empty_slots} empty slot{autofillPreview.main_first.empty_slots === 1 ? '' : 's'} on {autofillPreview.main_first.days.join(', ')}. The accounts share one vault and a draft locks its photos, so the account that fills first takes the best ones.
              </div>
            )}

            {/* GIF ratio — live */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#ddd', fontWeight: 600 }}>GIF target</span>
                <span style={{ fontSize: '13px', color: '#5eead4', fontWeight: 700 }}>{autofillGifPct}%</span>
              </div>
              <input type="range" min="0" max="100" step="5" value={autofillGifPct}
                onChange={(e) => setAutofillGifPct(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: '#ff4500' }} />
              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                {autofillPreview ? `Suggested ${autofillPreview.suggested_gif_pct}% — ${autofillPreview.suggested_gif_reason}.` : 'Best-effort — capped by how many GIFs are available.'}
              </div>
            </div>

            {/* Carousels */}
            <div style={{ padding: '10px 0', borderTop: '1px solid #222' }}>
              <div style={{ fontSize: '13px', color: '#ddd', fontWeight: 600 }}>Carousels</div>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>2–4 images from one folder in a single post, only where a matching set exists</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {[['auto', `Where the sub allows galleries${autofillPreview ? ` (${Math.max(0, Math.min(autofillPreview.gallery_slots, autofillPreview.empty_slots - Math.round(autofillGifPct / 100 * autofillPreview.empty_slots)))} slots after the GIFs)` : ''}`], ['none', 'None'], ['custom', 'Custom']].map(([k, l]) => (
                  <button key={k} onClick={() => setAutofillMode(k)} style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '14px', cursor: 'pointer', fontFamily: 'inherit', background: autofillMode === k ? '#1e3a5f' : '#1a1a1a', color: autofillMode === k ? '#fff' : '#888', border: `1px solid ${autofillMode === k ? '#3b82f6' : '#333'}` }}>{l}</button>
                ))}
                {autofillMode === 'custom' && (
                  <input type="number" min="0" max="20" value={autofillCarousels} onChange={e => setAutofillCarousels(Math.max(0, parseInt(e.target.value) || 0))}
                    style={{ width: '60px', fontSize: '12px', padding: '4px 6px', background: '#0f0f0f', border: '1px solid #333', borderRadius: '6px', color: '#fff', fontFamily: 'inherit' }} />
                )}
              </div>
            </div>

            {/* Links / video — coming soon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #222', opacity: 0.5, marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#ddd', fontWeight: 600 }}>Include video / links (RedGifs)</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Post videos as RedGifs links</div>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#f59e0b', border: '1px solid #5a4400', borderRadius: '6px', padding: '2px 8px' }}>COMING SOON</span>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setAutofillModal(null)}>Cancel</button>
              <button onClick={() => autofillContent(autofillModal, autofillGifPct, autofillMode === 'auto' ? 'auto' : autofillMode === 'none' ? 0 : autofillCarousels)}
                style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: '#ff4500', color: '#fff', fontFamily: 'inherit' }}>
                ✨ Fill session
              </button>
            </div>
          </div>
        </div>
      )}

      {previewSession && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: '#1a1a1a', borderBottom: '1px solid #2a2a2a', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0 }}>Preview — {previewSession.name || getAccountName(previewSession.account_id)}</h2>
              <span style={{ fontSize: '13px', color: '#aaaaaa' }}>{(previewSession.scheduled_posts || []).length} posts</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { updateSessionStatus(previewSession.id, 'ready'); setPreviewSession(null); }} style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600' }}>
                ✓ Confirm & Mark Ready
              </button>
              <button onClick={() => setPreviewSession(null)} style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
            {(previewSession.scheduled_posts || []).length === 0 ? (
              <p style={{ color: '#aaaaaa', textAlign: 'center' }}>No posts added to this session yet.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {(previewSession.scheduled_posts || []).slice().sort((a, b) => { if (a.is_profile_post && !b.is_profile_post) return -1; if (!a.is_profile_post && b.is_profile_post) return 1; return new Date(a.created_at) - new Date(b.created_at); }).map(post => (
                  <div key={post.id} style={{ background: '#0f0f0f', borderRadius: '10px', border: '1px solid #2a2a2a', overflow: 'hidden' }}>
                    {post.content_url ? (
                      <div style={{ background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px', overflow: 'hidden' }}>
                        {(post.carousel_items && post.carousel_items.length > 1) ? (
                          <div style={{ display: 'flex', gap: '4px', padding: '12px', overflowX: 'auto', width: '100%' }}>
                            {post.carousel_items.map((ci, i) => (
                              <img key={ci.vault_item_id || i} src={ci.thumbnail_url || ci.content_url} onError={(e) => { if (ci.content_url && e.target.src !== ci.content_url) e.target.src = ci.content_url; else e.target.style.display = 'none'; }} alt=""
                                onClick={() => setLightbox({ sessionId: previewSession.id, postId: post.id, index: i })} title="Click to enlarge"
                                style={{ height: '180px', objectFit: 'contain', borderRadius: '6px', cursor: 'zoom-in',
                                  border: i === 0 ? '2px solid #2563eb' : '1px solid #2a2a2a', flexShrink: 0 }} />
                            ))}
                          </div>
                        ) : (
                          <img key={post.vault_item_id || post.content_url} src={post.thumbnail_url || post.content_url} alt={post.title}
                            onLoad={(e) => { e.target.style.display = 'block'; }}
                            onError={(e) => { if (post.content_url && e.target.src !== post.content_url) e.target.src = post.content_url; else e.target.style.display = 'none'; }}
                            onClick={() => setLightbox({ sessionId: previewSession.id, postId: post.id, index: 0 })} title="Click to enlarge"
                            style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', cursor: 'zoom-in' }} />
                        )}
                      </div>
                    ) : (
                      <div style={{ background: '#1a1a1a', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555' }}>No content selected</div>
                    )}
                    <div style={{ padding: '12px' }}>
                      <div style={{ fontSize: '12px', color: post.is_profile_post ? '#8b5cf6' : '#ff4500', fontWeight: '600', marginBottom: '6px' }}>
                        {post.is_profile_post ? '📌 Profile Post · u/' + (accounts.find(a => a.id === previewSession.account_id)?.reddit_username || '') : (post.subreddits?.name || getSubredditName(post.subreddit_id))}
                      </div>
                      <div style={{ fontSize: '13px', color: post.title ? '#ffffff' : '#555' }}>{post.title || 'No title set'}</div>
                      {post.pick_reason && <div style={{ fontSize: '11px', color: '#777', marginTop: '4px' }}>🎯 picked: {post.pick_reason}</div>}
                      <div style={{ marginTop: '10px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {post.content_url && (
                          <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }}
                            onClick={() => setLightbox({ sessionId: previewSession.id, postId: post.id, index: 0 })}>🔍 Enlarge</button>
                        )}
                        {previewSession.status !== 'ready' && post.subreddit_id && !post.is_profile_post && (
                          <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }} disabled={repicking === post.id}
                            title={(post.carousel_items || []).length > 1 ? 'Build a different carousel from another matching folder' : 'Swap in a different matched item'}
                            onClick={() => repickPost(previewSession.id, post.id)}>
                            {repicking === post.id ? '…' : (post.carousel_items || []).length > 1 ? '🎲 Re-roll carousel' : '🎲 Repick'}
                          </button>
                        )}
                        {previewSession.status !== 'ready' && post.content_url && (
                          <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px', color: '#f87171' }}
                            onClick={() => removeContent(previewSession.id, post.id)}>
                            🗑 {(post.carousel_items || []).length > 1 ? 'Remove all' : 'Remove'}
                          </button>
                        )}
                        <button className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={() => openVaultPicker(previewSession, post)}>📁 Swap from Vault</button>
                        <label style={{ fontSize: '11px', padding: '4px 10px', background: '#2a2a2a', color: '#aaaaaa', borderRadius: '6px', cursor: 'pointer', border: '1px solid #444' }}>
                          💻 Swap from Drive
                          <input type="file" accept="image/*,video/gif" style={{ display: 'none' }} onChange={async e => {
                            if (!e.target.files[0]) return;
                            await uploadFile(previewSession.id, post.id, e.target.files[0]);
                            const updated = sessions.find(s => s.id === previewSession.id);
                            if (updated) setPreviewSession({ ...updated });
                          }} />
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {lightbox && (() => {
        const lbSession = sessions.find(s => s.id === lightbox.sessionId);
        const lbPost = lbSession?.scheduled_posts?.find(p => p.id === lightbox.postId);
        if (!lbPost || !lbPost.content_url) return null;
        return (
          <MediaLightbox
            post={lbPost}
            subName={lbPost.is_profile_post ? '📌 Profile post' : (lbPost.subreddits?.name || getSubredditName(lbPost.subreddit_id))}
            index={lightbox.index}
            locked={lbSession.status === 'ready' || lbSession.status === 'posted' || lbSession.status === 'completed'}
            busy={repicking === lbPost.id}
            onIndex={(k) => setLightbox(l => l && { ...l, index: k })}
            onClose={() => setLightbox(null)}
            onRepick={(k) => repickPost(lbSession.id, lbPost.id, k)}
            onRemove={async (k) => {
              const n = (lbPost.carousel_items || []).length;
              await removeContent(lbSession.id, lbPost.id, k);
              // stay open on the carousel while images are left; close otherwise
              if (k !== null && n > 2) setLightbox(l => l && { ...l, index: Math.min(k, n - 2) });
              else setLightbox(null);
            }}
          />
        );
      })()}

{vaultModal && (() => {
  const key = `${vaultModal.accountId}-${vaultModal.subredditId}`;
  const allItems = (vaultData[key] || []).filter(i => i.status !== 'rejected');
  // What the target sub can take — items it can't accept are greyed out.
  const modalPost = [...sessions, previewSession].filter(Boolean)
    .flatMap(ss => ss.scheduled_posts || []).find(pp => pp.id === vaultModal.postId);
  const modalCaps = modalPost?.sub_caps || null;

  const getMediaType = (filename) => {
    if (!filename) return 'image';
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'gif') return 'gif';
    if (['mp4', 'mov', 'webm', 'avi'].includes(ext)) return 'video';
    if (['jpg', 'jpeg', 'png', 'webp', 'bmp'].includes(ext)) return 'image';
    return 'other';
  };

  const getFolderName = (folderId) => {
    if (!folderId) return 'Unfiled';
    const folder = vaultFolders.find(f => f.id === folderId);
    return folder ? folder.name : 'Unknown';
  };

  const searchTerm = vaultPickerSearch.toLowerCase().trim();

  // ── STEP 1: Search filter (across all text fields) ──
  const matchesSearch = (item) => {
    if (!searchTerm) return true;
    return (item.title_suggestion || '').toLowerCase().includes(searchTerm) ||
           (item.image_description || '').toLowerCase().includes(searchTerm) ||
           (item.content_tags || []).some(t => t.toLowerCase().includes(searchTerm));
  };

  // ── STEP 2: Apply search + media + tag filters to get searchMatched items ──
  const searchMatched = allItems.filter(item => {
    if (!matchesSearch(item)) return false;

    // Media type filter
    if (vaultMediaFilter !== 'all') {
      const type = getMediaType(item.original_filename);
      if (vaultMediaFilter === 'images' && type !== 'image') return false;
      if (vaultMediaFilter === 'gifs' && type !== 'gif') return false;
      if (vaultMediaFilter === 'videos' && type !== 'video') return false;
    }

    // Content tag filter (explicit tag pill selection)
    if (vaultPickerTags.length > 0) {
      if (!vaultPickerTags.every(t => (item.content_tags || []).includes(t))) return false;
    }

    return true;
  });

  // ── STEP 3: Compute folder hit counts from search results ──
  const folderHits = new Map();
  searchMatched.forEach(item => {
    const fKey = item.folder_id || 'unfiled';
    folderHits.set(fKey, (folderHits.get(fKey) || 0) + 1);
  });

  // ── STEP 4: Filter folders to only those with hits (when searching) ──
  const topLevelFolders = vaultFolders.filter(f => !f.parent_id).sort((a, b) => {
    switch (vaultFolderSortBy) {
      case 'name_asc': return a.name.localeCompare(b.name);
      case 'name_desc': return b.name.localeCompare(a.name);
      case 'date_new': return new Date(b.created_at) - new Date(a.created_at);
      case 'date_old': return new Date(a.created_at) - new Date(b.created_at);
      case 'items_most': return (b.item_count || 0) - (a.item_count || 0);
      case 'items_least': return (a.item_count || 0) - (b.item_count || 0);
      default: return 0;
    }
  });

  const visibleFolders = searchTerm
    ? topLevelFolders.filter(f => folderHits.has(f.id))
    : topLevelFolders;

  const showUnfiled = searchTerm
    ? folderHits.has('unfiled')
    : vaultUnfiledCount > 0;

  // Auto-deselect folders that have zero matches when searching
  const effectiveSelectedFolders = vaultSelectedFolderIds.filter(id => {
    if (!searchTerm) return true;
    return folderHits.has(id);
  });

  // ── STEP 5: Apply folder selection on top of search results ──
  const filtered = searchMatched.filter(item => {
    if (effectiveSelectedFolders.length === 0) return true;
    const isUnfiledSelected = effectiveSelectedFolders.includes('unfiled');
    const folderIdsSelected = effectiveSelectedFolders.filter(id => id !== 'unfiled');
    const itemInSelectedFolder = folderIdsSelected.includes(item.folder_id);
    const itemIsUnfiled = !item.folder_id && isUnfiledSelected;
    return itemInSelectedFolder || itemIsUnfiled;
  }).sort((a, b) => {
    switch (vaultSortBy) {
      case 'name_asc': return (a.title_suggestion || a.original_filename || '').localeCompare(b.title_suggestion || b.original_filename || '');
      case 'name_desc': return (b.title_suggestion || b.original_filename || '').localeCompare(a.title_suggestion || a.original_filename || '');
      case 'date_new': return new Date(b.created_at) - new Date(a.created_at);
      case 'date_old': return new Date(a.created_at) - new Date(b.created_at);
      case 'modified_new': return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      case 'modified_old': return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
      default: return 0;
    }
  });

  // ── STEP 6: Which content tags match the search term? ──
  const tagMatchesSearch = (tagName) => {
    if (!searchTerm) return false;
    return tagName.toLowerCase().includes(searchTerm);
  };

  const toggleFolderSelect = (folderId) => {
    setVaultSelectedFolderIds(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
    setVaultPickerPage(1);
  };

  const totalPages = Math.ceil(filtered.length / vaultPickerPerPage);
  const paginated = filtered.slice((vaultPickerPage - 1) * vaultPickerPerPage, vaultPickerPage * vaultPickerPerPage);

  const folderSortOptions = [
    { value: 'name_asc', label: 'Name (A→Z)' },
    { value: 'name_desc', label: 'Name (Z→A)' },
    { value: 'date_new', label: 'Newest' },
    { value: 'date_old', label: 'Oldest' },
    { value: 'items_most', label: 'Most items' },
    { value: 'items_least', label: 'Fewest items' }
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: '#0a0a0a', borderRadius: '14px',
        width: '94%', maxWidth: '1000px', height: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        border: '1px solid #1f1f1f'
      }}>

        {/* ── Header ── */}
        <div style={{
          borderBottom: '1px solid #1f1f1f', padding: '18px 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#fff' }}>Pick from Vault</h2>
          <button
            onClick={() => setVaultModal(null)}
            style={{
              background: 'transparent', border: 'none', color: '#666',
              fontSize: '22px', cursor: 'pointer', padding: '4px 8px',
              borderRadius: '6px', lineHeight: 1
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = '#1f1f1f'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#666'; e.currentTarget.style.background = 'transparent'; }}
          >×</button>
        </div>

        {/* ── Search + Tags ── */}
        <div style={{ borderBottom: '1px solid #1f1f1f', padding: '14px 22px', flexShrink: 0 }}>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <span style={{
              position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
              color: '#555', fontSize: '14px', pointerEvents: 'none'
            }}>🔍</span>
            <input
              placeholder="Search descriptions, titles, tags..."
              value={vaultPickerSearch}
              onChange={e => { setVaultPickerSearch(e.target.value); setVaultPickerPage(1); }}
              style={{
                width: '100%', background: '#141414', border: '1px solid #1f1f1f',
                borderRadius: '10px', padding: '10px 14px 10px 38px',
                color: '#fff', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
              }}
            />
            {vaultPickerSearch && (
              <span
                onClick={() => { setVaultPickerSearch(''); setVaultPickerPage(1); }}
                style={{
                  position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                  color: '#555', fontSize: '16px', cursor: 'pointer', lineHeight: 1
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}
              >×</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Media type pills */}
            {[
              { key: 'all', label: 'All' },
              { key: 'images', label: '🖼 Images' },
              { key: 'gifs', label: '🎞 GIFs' },
              { key: 'videos', label: '🎬 Videos' }
            ].map(m => (
              <span
                key={m.key}
                onClick={() => { setVaultMediaFilter(m.key); setVaultPickerPage(1); }}
                style={{
                  padding: '5px 14px', borderRadius: '20px', fontSize: '12px',
                  cursor: 'pointer', fontWeight: '500', transition: 'all 0.15s',
                  background: vaultMediaFilter === m.key ? '#2563eb' : '#1a1a1a',
                  color: vaultMediaFilter === m.key ? '#fff' : '#999',
                  border: vaultMediaFilter === m.key ? '1px solid #2563eb' : '1px solid #2a2a2a'
                }}
              >{m.label}</span>
            ))}
            {/* Divider */}
            {contentTags.length > 0 && (
              <span style={{ width: '1px', height: '20px', background: '#2a2a2a', margin: '0 4px' }} />
            )}
            {/* Content tag pills — highlight when matching search */}
            {contentTags.map(tag => {
              const active = vaultPickerTags.includes(tag.name);
              const matchesCurrentSearch = tagMatchesSearch(tag.name);
              return (
                <span key={tag.id} onClick={() => {
                  setVaultPickerTags(prev => active ? prev.filter(t => t !== tag.name) : [...prev, tag.name]);
                  setVaultPickerPage(1);
                }} style={{
                  padding: '5px 14px', borderRadius: '20px', fontSize: '12px',
                  cursor: 'pointer', fontWeight: '500', transition: 'all 0.15s',
                  background: active ? '#ff4500' : matchesCurrentSearch ? '#2a1a00' : '#1a1a1a',
                  color: active ? '#fff' : matchesCurrentSearch ? '#f59e0b' : '#999',
                  border: active ? '1px solid #ff4500' : matchesCurrentSearch ? '1px solid #f59e0b' : '1px solid #2a2a2a',
                  boxShadow: matchesCurrentSearch && !active ? '0 0 8px rgba(245,158,11,0.15)' : 'none'
                }}>{tag.name}</span>
              );
            })}
          </div>
        </div>

        {/* ── Folder strip (horizontal scroll) ── */}
        <div style={{ borderBottom: '1px solid #1f1f1f', padding: '12px 22px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', color: '#666' }}>
              📁 {visibleFolders.length}{searchTerm ? ` of ${topLevelFolders.length}` : ''} folders
              {effectiveSelectedFolders.length > 0 && (
                <span style={{ color: '#2563eb' }}> · {effectiveSelectedFolders.length} selected</span>
              )}
            </span>

            <div style={{ marginLeft: 'auto', position: 'relative' }}>
              <button
                onClick={() => setShowFolderSortDropdown(!showFolderSortDropdown)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '5px 12px', background: '#1a1a1a', border: '1px solid #2a2a2a',
                  borderRadius: '8px', color: '#999', fontSize: '12px', cursor: 'pointer'
                }}
              >
                {folderSortOptions.find(o => o.value === vaultFolderSortBy)?.label || 'Sort'}
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>
              {showFolderSortDropdown && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                    onClick={() => setShowFolderSortDropdown(false)}
                  />
                  <div style={{
                    position: 'absolute', right: 0, top: '100%', marginTop: '4px',
                    background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 20, minWidth: '150px',
                    overflow: 'hidden'
                  }}>
                    {folderSortOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => { setVaultFolderSortBy(option.value); setShowFolderSortDropdown(false); }}
                        style={{
                          padding: '8px 14px', fontSize: '12px', cursor: 'pointer',
                          background: vaultFolderSortBy === option.value ? '#2563eb' : 'transparent',
                          color: vaultFolderSortBy === option.value ? '#fff' : '#ccc'
                        }}
                        onMouseEnter={e => { if (vaultFolderSortBy !== option.value) e.currentTarget.style.background = '#222'; }}
                        onMouseLeave={e => { if (vaultFolderSortBy !== option.value) e.currentTarget.style.background = 'transparent'; }}
                      >{option.label}</div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {effectiveSelectedFolders.length > 0 && (
              <button
                onClick={() => { setVaultSelectedFolderIds([]); setVaultPickerPage(1); }}
                style={{
                  fontSize: '12px', color: '#2563eb', background: 'transparent',
                  border: 'none', cursor: 'pointer', padding: '4px 8px'
                }}
                onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
              >Clear filter</button>
            )}
          </div>

          {/* Horizontal scrolling folder chips */}
          {(visibleFolders.length > 0 || showUnfiled) ? (
            <div style={{
              display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px',
              scrollbarWidth: 'thin', scrollbarColor: '#333 transparent'
            }}>
              {visibleFolders.map(folder => {
                const isSelected = effectiveSelectedFolders.includes(folder.id);
                const hitCount = folderHits.get(folder.id) || 0;
                return (
                  <button
                    key={folder.id}
                    onClick={() => toggleFolderSelect(folder.id)}
                    style={{
                      flexShrink: 0, padding: '8px 14px', borderRadius: '10px',
                      cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                      background: isSelected ? '#141414' : '#111',
                      border: isSelected ? '1.5px solid #2563eb' : '1px solid #1f1f1f',
                      color: isSelected ? '#fff' : '#ccc'
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#333'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = isSelected ? '#2563eb' : '#1f1f1f'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>{folder.name}</span>
                      <span style={{ fontSize: '11px', color: searchTerm ? '#f59e0b' : '#666' }}>
                        {searchTerm ? hitCount : folder.item_count}
                      </span>
                    </div>
                  </button>
                );
              })}
              {/* Unfiled chip */}
              {showUnfiled && (
                <button
                  onClick={() => toggleFolderSelect('unfiled')}
                  style={{
                    flexShrink: 0, padding: '8px 14px', borderRadius: '10px',
                    cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                    background: effectiveSelectedFolders.includes('unfiled') ? '#141414' : '#111',
                    border: effectiveSelectedFolders.includes('unfiled') ? '1.5px solid #2563eb' : '1px solid #1f1f1f',
                    color: effectiveSelectedFolders.includes('unfiled') ? '#fff' : '#999'
                  }}
                  onMouseEnter={e => { if (!effectiveSelectedFolders.includes('unfiled')) e.currentTarget.style.borderColor = '#333'; }}
                  onMouseLeave={e => { if (!effectiveSelectedFolders.includes('unfiled')) e.currentTarget.style.borderColor = effectiveSelectedFolders.includes('unfiled') ? '#2563eb' : '#1f1f1f'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}>Unfiled</span>
                    <span style={{ fontSize: '11px', color: searchTerm ? '#f59e0b' : '#666' }}>
                      {searchTerm ? (folderHits.get('unfiled') || 0) : vaultUnfiledCount}
                    </span>
                  </div>
                </button>
              )}
            </div>
          ) : searchTerm ? (
            <div style={{ fontSize: '12px', color: '#555', padding: '4px 0' }}>
              No folders contain matching content
            </div>
          ) : null}
        </div>

        {/* ── Photos header bar ── */}
        <div style={{
          borderBottom: '1px solid #1f1f1f', padding: '10px 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <span style={{ fontSize: '13px', color: '#888' }}>
            {searchTerm ? (
              <>
                <span style={{ color: '#fff', fontWeight: '600' }}>{filtered.length}</span>
                {' '}result{filtered.length !== 1 ? 's' : ''}
                {' '}for "<span style={{ color: '#f59e0b' }}>{vaultPickerSearch}</span>"
                {effectiveSelectedFolders.length > 0 && (
                  <span style={{ color: '#666' }}> in {effectiveSelectedFolders.length} folder{effectiveSelectedFolders.length !== 1 ? 's' : ''}</span>
                )}
              </>
            ) : (
              <>
                Showing <span style={{ color: '#fff', fontWeight: '600' }}>{filtered.length}</span> items
                {effectiveSelectedFolders.length > 0 && (
                  <span style={{ color: '#666' }}> from {effectiveSelectedFolders.length} folder{effectiveSelectedFolders.length !== 1 ? 's' : ''}</span>
                )}
              </>
            )}
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={vaultSortBy}
              onChange={e => { setVaultSortBy(e.target.value); setVaultPickerPage(1); }}
              style={{
                fontSize: '12px', padding: '5px 10px', background: '#1a1a1a',
                color: '#999', border: '1px solid #2a2a2a', borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <option value="date_new">Newest upload</option>
              <option value="date_old">Oldest upload</option>
              <option value="modified_new">Recently modified</option>
              <option value="modified_old">Least modified</option>
              <option value="name_asc">A→Z</option>
              <option value="name_desc">Z→A</option>
            </select>
            <select
              value={vaultPickerPerPage}
              onChange={e => { setVaultPickerPerPage(parseInt(e.target.value)); setVaultPickerPage(1); }}
              style={{
                fontSize: '12px', padding: '5px 10px', background: '#1a1a1a',
                color: '#999', border: '1px solid #2a2a2a', borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            {['grid', 'list'].map(v => (
              <button key={v} onClick={() => setVaultPickerView(v)} style={{
                padding: '5px 12px', fontSize: '12px', borderRadius: '8px', border: 'none',
                cursor: 'pointer', transition: 'all 0.15s',
                background: vaultPickerView === v ? '#2563eb' : '#1a1a1a',
                color: vaultPickerView === v ? '#fff' : '#999'
              }}>{v === 'grid' ? '⊞' : '≡'}</button>
            ))}
          </div>
        </div>

        {/* ── Photos grid / list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
          {paginated.length === 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: '#555', textAlign: 'center', padding: '40px'
            }}>
              <div>
                <p style={{ fontSize: '14px', margin: '0 0 6px 0' }}>
                  {searchTerm ? `No items match "${vaultPickerSearch}"` : 'No items found'}
                </p>
                <p style={{ fontSize: '12px', margin: 0, color: '#444' }}>
                  {searchTerm ? 'Try a different search term or clear filters' : 'Try adjusting your filters'}
                </p>
              </div>
            </div>
          ) : vaultPickerView === 'grid' ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '10px'
            }}>
              {paginated.map(item => {
                const r = vaultRestrictions[item.id];
                const inCurrentSession = r?.inCurrentSession;
                const usedInSubreddit = r?.recentSubreddits?.some(s => s.subreddit_id === vaultModal.subredditId);
                const scheduledForSameSub = r?.scheduledSubreddits?.some(s => s.subreddit_id === vaultModal.subredditId);
                const isDraftedElsewhere = r?.draftedElsewhere?.length > 0;
                const isBlocked = inCurrentSession || scheduledForSameSub || (usedInSubreddit && role !== 'admin' && role !== 'super_admin') || isDraftedElsewhere;
                const borderColor = inCurrentSession ? '#ef4444' : scheduledForSameSub ? '#ef4444' : usedInSubreddit ? '#ef4444' : isDraftedElsewhere ? '#f59e0b' : 'transparent';
                const selectionIndex = vaultSelection.findIndex(v => v.id === item.id);
                const isSelected = selectionIndex >= 0;
                const isGifOrVideo = getMediaType(item.original_filename) === 'gif' || getMediaType(item.original_filename) === 'video';
                // A 2nd image makes a gallery — blocked where the sub takes none.
                const galleryIssue = !isGifOrVideo && vaultSelection.length > 0 && !isSelected
                  ? contentFitIssue('carousel', modalCaps, { count: vaultSelection.length + 1 }) : null;
                const carouselBlocked = (isGifOrVideo && vaultSelection.length > 0) || !!galleryIssue;
                const typeIssue = contentFitIssue(getMediaType(item.original_filename) === 'other' ? 'image' : getMediaType(item.original_filename), modalCaps, { explicitness: item.attributes?.explicitness });

                return (
                  <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div
                      onClick={() => {
                        if (isBlocked || carouselBlocked || typeIssue || vaultPreviewItem) return;
                        const type = getMediaType(item.original_filename);
                        if (type === 'gif' || type === 'video') {
                          if (vaultSelection.length > 0) return;
                          setVaultPreviewItem(item);
                        } else {
                          if (isSelected) {
                            setVaultSelection(prev => prev.filter(v => v.id !== item.id));
                          } else if (vaultSelection.length < 5) {
                            setVaultSelection(prev => [...prev, item]);
                          }
                        }
                      }}
                      title={typeIssue ? `Can't post this here: ${typeIssue}` : galleryIssue ? `Can't add more: ${galleryIssue}` : undefined}
                      style={{
                        position: 'relative', paddingTop: '100%', borderRadius: '10px',
                        overflow: 'hidden', cursor: isBlocked || carouselBlocked || typeIssue ? 'not-allowed' : 'pointer',
                        opacity: isBlocked || carouselBlocked || typeIssue ? 0.2 : 1, transition: 'all 0.15s',
                        border: isSelected ? '2px solid #2563eb' : (borderColor !== 'transparent' ? `2px solid ${borderColor}` : '2px solid transparent'),
                        background: '#141414'
                      }}
                      onMouseEnter={e => { if (!isBlocked && !carouselBlocked && !typeIssue) e.currentTarget.style.borderColor = '#2563eb'; }}
                      onMouseLeave={e => { if (!isBlocked && !carouselBlocked && !typeIssue) e.currentTarget.style.borderColor = isSelected ? '#2563eb' : borderColor; }}
                    >
                      <img
                        src={item.thumbnail_url || item.file_url} onError={(e) => { if (item.file_url && e.target.src !== item.file_url) e.target.src = item.file_url; else e.target.style.display = 'none'; }}
                        alt=""
                        loading="lazy"
                        style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Carousel selection badge */}
                      {isSelected && (
                        <div style={{
                          position: 'absolute', top: '6px', right: '6px',
                          width: '24px', height: '24px', borderRadius: '50%',
                          background: '#2563eb', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontWeight: '700',
                          border: '2px solid #fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                          zIndex: 5
                        }}>
                          {selectionIndex + 1}
                        </div>
                      )}
                      {carouselBlocked && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(0,0,0,0.7)', zIndex: 5,
                          fontSize: '11px', color: '#888', textAlign: 'center', padding: '8px'
                        }}>
                          Images selected —<br/>carousel mode
                        </div>
                      )}
                      {/* Media type badge */}
                      {getMediaType(item.original_filename) === 'gif' && (
                        <span style={{
                          position: 'absolute', top: '6px', left: '6px',
                          background: 'rgba(37,99,235,0.9)', color: '#fff',
                          padding: '2px 8px', borderRadius: '6px', fontSize: '9px', fontWeight: '700'
                        }}>GIF</span>
                      )}
                      {getMediaType(item.original_filename) === 'video' && (
                        <div style={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%,-50%)',
                          width: '30px', height: '30px', borderRadius: '50%',
                          background: 'rgba(0,0,0,0.6)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center'
                        }}>
                          <span style={{ color: '#fff', fontSize: '12px', marginLeft: '2px' }}>▶</span>
                        </div>
                      )}
                      {/* Restriction badges */}
                      {scheduledForSameSub && !inCurrentSession && (
                        <div style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(239,68,68,0.95)', color: '#fff', fontSize: '8px', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>📅 Scheduled for this sub</div>
                      )}
                      {usedInSubreddit && !scheduledForSameSub && !inCurrentSession && (
                        <div style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(239,68,68,0.9)', color: '#fff', fontSize: '8px', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>🔴 Used in 30d</div>
                      )}
                      {inCurrentSession && (
                        <div style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(239,68,68,0.95)', color: '#fff', fontSize: '8px', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>🔴 Already in session</div>
                      )}
                      {isDraftedElsewhere && !usedInSubreddit && !inCurrentSession && (
                        <div style={{ position: 'absolute', top: '6px', left: '6px', background: 'rgba(245,158,11,0.9)', color: '#fff', fontSize: '8px', padding: '3px 8px', borderRadius: '6px', fontWeight: '700' }}>📌 In {r.draftedElsewhere.length} session{r.draftedElsewhere.length > 1 ? 's' : ''}</div>
                      )}
                      {/* Spoof cycle badge */}
                      {item.spoof_cycle > 0 && (
                        <span style={{ position: 'absolute', bottom: '6px', right: '6px', background: 'rgba(0,0,0,0.8)', color: '#f59e0b', fontSize: '9px', padding: '2px 6px', borderRadius: '6px' }}>cycle {item.spoof_cycle}</span>
                      )}
                      {/* Content tags */}
                      {(item.content_tags || []).length > 0 && (
                        <div style={{ position: 'absolute', bottom: '6px', left: '6px', display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                          {(item.content_tags || []).map(tag => (
                            <span key={tag} style={{ background: 'rgba(255,69,0,0.8)', color: '#fff', fontSize: '8px', padding: '2px 5px', borderRadius: '4px' }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Folder + title label under thumbnail */}
                    <div style={{ fontSize: '10px', color: '#555', paddingLeft: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#444' }}>{getFolderName(item.folder_id)}</span>
                      {item.title_suggestion && (
                        <span style={{ color: '#666', marginLeft: '4px' }}>· {item.title_suggestion}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── List view ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {paginated.map(item => {
                const r = vaultRestrictions[item.id];
                const inCurrentSession = r?.inCurrentSession;
                const usedInSubreddit = r?.recentSubreddits?.some(s => s.subreddit_id === vaultModal.subredditId);
                const scheduledForSameSub = r?.scheduledSubreddits?.some(s => s.subreddit_id === vaultModal.subredditId);
                const isDraftedElsewhere = r?.draftedElsewhere?.length > 0;
                const isBlocked = inCurrentSession || scheduledForSameSub || (usedInSubreddit && role !== 'admin' && role !== 'super_admin') || isDraftedElsewhere;
                const borderColor = inCurrentSession ? '#ef4444' : scheduledForSameSub ? '#ef4444' : usedInSubreddit ? '#ef4444' : isDraftedElsewhere ? '#f59e0b' : '#1f1f1f';
                const lType = getMediaType(item.original_filename);
                const listSelected = vaultSelection.some(v => v.id === item.id);
                const typeIssue = contentFitIssue(lType === 'other' ? 'image' : lType, modalCaps, { explicitness: item.attributes?.explicitness })
                  || (lType !== 'gif' && lType !== 'video' && vaultSelection.length > 0 && !listSelected
                    ? contentFitIssue('carousel', modalCaps, { count: vaultSelection.length + 1 }) : null);

                return (
                  <div key={item.id}
                    title={typeIssue ? `Can't post this here: ${typeIssue}` : undefined}
                    onClick={() => {
                      if (isBlocked || typeIssue || vaultPreviewItem) return;
                      const type = getMediaType(item.original_filename);
                      if (type === 'gif' || type === 'video') {
                        setVaultPreviewItem(item);
                      } else {
                        pickFromVault(vaultModal.sessionId, vaultModal.postId, item);
                      }
                    }}
                    style={{
                      display: 'flex', gap: '12px', alignItems: 'center',
                      background: '#111', borderRadius: '10px', padding: '10px 14px',
                      border: `1.5px solid ${borderColor}`,
                      cursor: isBlocked || typeIssue ? 'not-allowed' : 'pointer',
                      opacity: isBlocked || typeIssue ? 0.35 : 1, transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { if (!isBlocked) e.currentTarget.style.borderColor = '#2563eb'; }}
                    onMouseLeave={e => { if (!isBlocked) e.currentTarget.style.borderColor = borderColor; }}
                  >
                    <img src={item.thumbnail_url || item.file_url} onError={(e) => { if (item.file_url && e.target.src !== item.file_url) e.target.src = item.file_url; else e.target.style.display = 'none'; }} alt="" loading="lazy" style={{
                      width: '52px', height: '52px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: '#fff', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title_suggestion || item.original_filename}
                      </div>
                      <div style={{ fontSize: '11px', color: '#555', marginTop: '3px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#666' }}>{getFolderName(item.folder_id)}</span>
                        {item.spoof_cycle > 0 && <span style={{ color: '#f59e0b' }}>cycle {item.spoof_cycle}</span>}
                        {scheduledForSameSub && !inCurrentSession && <span style={{ color: '#ef4444' }}>📅 Scheduled for this sub</span>}
                        {usedInSubreddit && !scheduledForSameSub && !inCurrentSession && <span style={{ color: '#ef4444' }}>🔴 Used in 30d</span>}
                        {inCurrentSession && <span style={{ color: '#ef4444' }}>🔴 Already in session</span>}
                        {isDraftedElsewhere && !usedInSubreddit && !inCurrentSession && <span style={{ color: '#f59e0b' }}>🟠 Drafted elsewhere</span>}
                      </div>
                      {(item.content_tags || []).length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '5px' }}>
                          {(item.content_tags || []).map(tag => (
                            <span key={tag} style={{ background: '#1a0800', color: '#ff4500', fontSize: '10px', padding: '2px 8px', borderRadius: '8px', border: '1px solid #ff4500' }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── GIF/Video Preview Popup ── */}
        {vaultPreviewItem && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)', zIndex: 1100,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }} onClick={() => setVaultPreviewItem(null)}>
            <div style={{ maxWidth: '90%', maxHeight: '70vh', marginBottom: '16px' }} onClick={e => e.stopPropagation()}>
              {getMediaType(vaultPreviewItem.original_filename) === 'video' ? (
                <div style={{
                  width: '85vw', maxWidth: '500px', height: '70vh', maxHeight: '700px',
                  background: '#000', borderRadius: '12px', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <video
                    src={vaultPreviewItem.file_url}
                    controls autoPlay loop
                    controlsList="nodownload noplaybackrate"
                    disablePictureInPicture playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              ) : (
                <img
                  src={vaultPreviewItem.file_url}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '12px' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }} onClick={e => e.stopPropagation()}>
              <button
                onClick={() => {
                  pickFromVault(vaultModal.sessionId, vaultModal.postId, vaultPreviewItem);
                  setVaultPreviewItem(null);
                }}
                style={{
                  background: '#2563eb', color: '#fff', border: 'none',
                  borderRadius: '10px', padding: '12px 28px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '600'
                }}
              >✓ Use this</button>
              <button
                onClick={() => setVaultPreviewItem(null)}
                style={{
                  background: 'transparent', color: '#999', border: '1px solid #444',
                  borderRadius: '10px', padding: '12px 28px', cursor: 'pointer',
                  fontSize: '14px'
                }}
              >Cancel</button>
            </div>
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
              {vaultPreviewItem.title_suggestion || vaultPreviewItem.original_filename}
            </div>
          </div>
        )}

        {/* ── Carousel preview & reorder ── */}
        {showSelectionPreview && vaultSelection.length > 0 && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)', zIndex: 1100,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: '#111', borderRadius: '14px', padding: '24px',
              width: '90%', maxWidth: '700px', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              border: '1px solid #1f1f1f'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '16px', color: '#fff' }}>
                  Carousel — {vaultSelection.length} Images
                </h2>
                <button onClick={() => setShowSelectionPreview(false)}
                  style={{ background: 'transparent', border: 'none', color: '#666', fontSize: '22px', cursor: 'pointer' }}>×</button>
              </div>

              <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                Drag to reorder or use arrows. First image will be the main thumbnail on Reddit.
              </div>

              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {vaultSelection.map((selItem, idx) => (
                  <div key={selItem.id} draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragIdx === null || dragIdx === idx) return;
                      setVaultSelection(prev => {
                        const copy = [...prev];
                        const [moved] = copy.splice(dragIdx, 1);
                        copy.splice(idx, 0, moved);
                        return copy;
                      });
                      setDragIdx(null);
                    }}
                    onDragEnd={() => setDragIdx(null)}
                    style={{
                      display: 'flex', gap: '14px', alignItems: 'center',
                      background: dragIdx === idx ? '#1a1a2a' : '#0a0a0a',
                      borderRadius: '10px', padding: '10px 14px',
                      border: idx === 0 ? '1.5px solid #2563eb' : '1.5px solid #1f1f1f',
                      cursor: 'grab', transition: 'background 0.15s',
                      opacity: dragIdx === idx ? 0.5 : 1
                    }}>
                    <div style={{ width: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                      <span style={{ fontSize: '14px', color: '#555', cursor: 'grab' }}>⠿</span>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: idx === 0 ? '#2563eb' : '#666' }}>{idx + 1}</span>
                    </div>
                    <img src={selItem.thumbnail_url || selItem.file_url} onError={(e) => { if (selItem.file_url && e.target.src !== selItem.file_url) e.target.src = selItem.file_url; else e.target.style.display = 'none'; }} alt=""
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: '#fff', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selItem.title_suggestion || selItem.original_filename || 'Untitled'}
                      </div>
                      {idx === 0 && <div style={{ fontSize: '11px', color: '#2563eb', marginTop: '4px' }}>★ Main thumbnail</div>}
                      {(selItem.content_tags || []).length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                          {(selItem.content_tags || []).map(tag => (
                            <span key={tag} style={{ background: '#1a0800', color: '#ff4500', fontSize: '10px', padding: '2px 8px', borderRadius: '8px', border: '1px solid #ff4500' }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
                      {idx > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); setVaultSelection(prev => { const c = [...prev]; [c[idx-1], c[idx]] = [c[idx], c[idx-1]]; return c; }); }}
                          style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #333', background: 'transparent', color: '#888', cursor: 'pointer' }}>↑</button>
                      )}
                      {idx < vaultSelection.length - 1 && (
                        <button onClick={(e) => { e.stopPropagation(); setVaultSelection(prev => { const c = [...prev]; [c[idx], c[idx+1]] = [c[idx+1], c[idx]]; return c; }); }}
                          style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #333', background: 'transparent', color: '#888', cursor: 'pointer' }}>↓</button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setVaultSelection(prev => prev.filter(v => v.id !== selItem.id)); if (vaultSelection.length <= 1) setShowSelectionPreview(false); }}
                        style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #7f1d1d', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>×</button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #1f1f1f' }}>
                <button onClick={() => setShowSelectionPreview(false)}
                  style={{ padding: '10px 20px', fontSize: '13px', borderRadius: '8px', border: '1px solid #333', background: 'transparent', color: '#999', cursor: 'pointer' }}>
                  ← Back to Vault
                </button>
                <button onClick={() => { pickFromVault(vaultModal.sessionId, vaultModal.postId, vaultSelection); setShowSelectionPreview(false); }}
                  style={{ padding: '10px 28px', fontSize: '14px', borderRadius: '10px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                  ✓ Create carousel ({vaultSelection.length} images)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Carousel selection bar ── */}
        {vaultSelection.length > 0 && (
          <div style={{
            borderTop: '2px solid #2563eb', padding: '14px 22px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#0a0a1a', flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', overflow: 'auto' }}>
              {vaultSelection.map((selItem, i) => (
                <div key={selItem.id} style={{ position: 'relative', flexShrink: 0 }}>
                  <img src={selItem.thumbnail_url || selItem.file_url} alt=""
                    style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #2563eb' }} />
                  <span style={{
                    position: 'absolute', top: '-6px', left: '-6px',
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: '#2563eb', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: '700', border: '2px solid #0a0a1a'
                  }}>{i + 1}</span>
                  <span onClick={(e) => { e.stopPropagation(); setVaultSelection(prev => prev.filter(v => v.id !== selItem.id)); }}
                    style={{
                      position: 'absolute', top: '-6px', right: '-6px',
                      width: '18px', height: '18px', borderRadius: '50%',
                      background: '#ef4444', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '700', cursor: 'pointer', border: '2px solid #0a0a1a'
                    }}>×</span>
                </div>
              ))}
              <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                {vaultSelection.length === 1 ? '1 image' : `${vaultSelection.length}/5 images (carousel)`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '16px' }}>
              <button onClick={() => setVaultSelection([])}
                style={{ padding: '8px 16px', fontSize: '12px', borderRadius: '8px', border: '1px solid #333', background: 'transparent', color: '#999', cursor: 'pointer' }}>
                Clear
              </button>
              <button onClick={() => {
                if (vaultSelection.length === 1) {
                  pickFromVault(vaultModal.sessionId, vaultModal.postId, vaultSelection);
                } else {
                  setShowSelectionPreview(true);
                }
              }}
                style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '8px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                {vaultSelection.length === 1 ? '✓ Use this image' : `✓ Confirm ${vaultSelection.length} images`}
              </button>
            </div>
          </div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{
            borderTop: '1px solid #1f1f1f', padding: '12px 22px',
            display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center',
            flexShrink: 0
          }}>
            <button
              disabled={vaultPickerPage === 1}
              onClick={() => setVaultPickerPage(p => p - 1)}
              style={{
                padding: '6px 16px', fontSize: '12px', borderRadius: '8px',
                border: '1px solid #2a2a2a', background: '#1a1a1a',
                color: vaultPickerPage === 1 ? '#333' : '#999',
                cursor: vaultPickerPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >← Prev</button>
            <span style={{ color: '#666', fontSize: '12px' }}>
              {vaultPickerPage} of {totalPages}
            </span>
            <button
              disabled={vaultPickerPage === totalPages}
              onClick={() => setVaultPickerPage(p => p + 1)}
              style={{
                padding: '6px 16px', fontSize: '12px', borderRadius: '8px',
                border: '1px solid #2a2a2a', background: '#1a1a1a',
                color: vaultPickerPage === totalPages ? '#333' : '#999',
                cursor: vaultPickerPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >Next →</button>
          </div>
        )}

      </div>
    </div>
  );
})()}

    </div>
  );
}

export default Scheduler;